// =====================================================================
// Mi6 AIP — Question Catalog FINAL (SSOT)
// Source: V3 live bank + Apr-30 expansion proposal + aerospace-questions.md
//         + industrial-questions.md + medical-questions.md
// Built by Mi6 Intelligence System — 2026-05-05
// Decisions applied: 2026-05-05 per bank-decisions-LOGGED.md
// =====================================================================
//
// Drop-in replacement for /aip-v3-supabase/shared/questions.ts.
// All exports preserved: QuestionFlag, QuestionOption, QuestionDef,
// INDUSTRY_KEYS, IndustryKey, ALL_QUESTIONS, getActiveQuestions,
// CpStatusValue, deriveCpStatus, cpRawPoints, cpMaxPoints,
// HuntItemDef, HUNT_CATALOG, getActiveHuntItems.
//
// NEW vs V3:
//   - INDUSTRY_KEYS adds "Medical Device"
//   - 19 new universal questions (expansion proposal)
//   - 24 new Aerospace questions (replaces 2 stubs)
//   - 25 new Industrial Manufacturing questions (extends 3 spine stubs)
//   - 24 new Medical Device questions (new vertical)
//   - QuestionDef gains optional subVertical field per SCHEMA-REFERENCE.md
//
// DECISIONS APPLIED (bank-decisions-LOGGED.md):
//   cp7_pipeline_coverage — RETIRED (manager metric, not deal-level signal)
//   cp1_read_across — MOVED to industry-conditional (Building Materials + Industrial Manufacturing)
//   cp1_leading_lagging — RETIRED (overlap with cp1_pain_source)
//   cp4_financial_review_integration — RETIRED (post-close checklist, not CP4 gate)
//   cp2_hr_coverage — REFRAMED (text updated, kept in place)
//   cp10_commitment_type — max points reduced 5 → 2
//
// WEIGHTS: Points preserved from source files for all live questions.
//   New questions carry points as specified in source documents.
//   Where source documents specified no point value, a TODO is noted.
//
// V4 sub-questions (AIP-V4-MFG-AUTOMOTIVE-TEMPLATE.md):
//   Deferred to separate sprint per Decision 4c.
// =====================================================================

export type QuestionFlag = "OK" | "AMBER" | "SOFT_STOP" | "HARD_STOP";

export interface QuestionOption {
  key: string;
  label: string;
  flag: QuestionFlag;
  points: number;
}

export interface QuestionDef {
  key: string;
  cp: number; // 1-9, or 0 / 0.5 for pre-gates
  label: string;
  helpText?: string;
  options: QuestionOption[];
  // null = always-on. else only surfaces when account.industry matches.
  // Accepts a single string or an array of strings for multi-industry triggers.
  industryTrigger?: string | string[] | null;
  // optional sub-vertical drill-down (per SCHEMA-REFERENCE.md)
  subVertical?: string | null;
  // optional: skip this question on fastlane
  skipOnFastlane?: boolean;
  // CP1 BANT mapping — which BANT letter this question provides evidence for
  bantLetter?: "Need" | "Budget" | "Authority" | "Timeline";
  // Optional message shown in the soft-stop card when a SOFT_STOP option is selected
  softStopMessage?: string;
}

// =====================================================================
// Industry keys — updated to add "Medical Device"
// Bryan greenlit launch of 6 verticals; Medical Device is net-new.
// =====================================================================
export const INDUSTRY_KEYS = [
  "Automotive",
  "Aerospace",
  "Building Materials",
  "Industrial Manufacturing",
  "Facility Management",
  "Medical Device",
] as const;
export type IndustryKey = (typeof INDUSTRY_KEYS)[number];

// =====================================================================
// CP 1 — BDR BANT Qualifier
// Rebuilt 2026-05-06 per Bryan's locked spec.
// Purpose: BDR-facing qualifier after first touch.
// Verdict: FORWARD / HOLD / ELIMINATED (see cp1Verdict in schema.ts)
// 13 questions total
// =====================================================================
const CP1_QUESTIONS: QuestionDef[] = [
  // ----- Project exists (HARD KILL on "No") -----
  {
    key: "cp1_project_exists",
    cp: 1,
    label: "Is there a real project here?",
    options: [
      {
        key: "yes_confirmed",
        label: "Yes — buyer has confirmed they're working on / planning a project",
        flag: "OK",
        points: 3,
      },
      {
        key: "maybe_interest",
        label: "Maybe — they've expressed interest but no project named yet",
        flag: "AMBER",
        points: 1,
      },
      {
        key: "no_project",
        label: "No — there's no project",
        flag: "HARD_STOP",
        points: 0,
      },
    ],
  },
  // ----- Customer status (routing flag, no scoring) -----
  {
    key: "cp1_customer_status",
    cp: 1,
    label: "New customer or existing?",
    options: [
      {
        key: "new_customer",
        label: "New customer — never bought from us",
        flag: "OK",
        points: 0,
      },
      {
        key: "existing_expansion",
        label: "Existing customer — expansion / upsell opportunity",
        flag: "OK",
        points: 0,
      },
    ],
  },
  // ----- Industry sector (HARD KILL on out-of-lane verticals) -----
  {
    key: "cp1_industry_sector",
    cp: 1,
    label: "What sector are they in?",
    helpText:
      "Manufacturing-adjacent includes anyone who builds, assembles, or runs a plant with significant headcount — heavy equipment, medical device, aerospace, industrial control systems, IoT hardware, automotive.",
    options: [
      {
        key: "manufacturing",
        label: "Manufacturing or manufacturing-adjacent",
        flag: "OK",
        points: 3,
      },
      {
        key: "hospitality",
        label: "Hospitality",
        flag: "HARD_STOP",
        points: 0,
      },
      {
        key: "energy",
        label: "Energy",
        flag: "HARD_STOP",
        points: 0,
      },
      {
        key: "government",
        label: "Government (local / state / federal)",
        flag: "HARD_STOP",
        points: 0,
      },
      {
        key: "other_unsure",
        label: "Other / unsure",
        flag: "SOFT_STOP",
        points: 0,
      },
    ],
  },
  // ----- Mfg sub-vertical (informational, no score impact) -----
  {
    key: "cp1_mfg_subvertical",
    cp: 1,
    label: "Which type of manufacturing?",
    helpText:
      "Cosmetic for today — drives template language and examples downstream. Does not affect scoring.",
    options: [
      { key: "automotive", label: "Automotive", flag: "OK", points: 0 },
      { key: "heavy_equipment", label: "Heavy Equipment", flag: "OK", points: 0 },
      { key: "medical_device", label: "Medical Device", flag: "OK", points: 0 },
      { key: "aerospace_defense", label: "Aerospace & Defense", flag: "OK", points: 0 },
      { key: "industrial_control", label: "Industrial Control Systems", flag: "OK", points: 0 },
      { key: "iot_hardware", label: "IoT Hardware", flag: "OK", points: 0 },
      { key: "other_mfg", label: "Other Manufacturing", flag: "OK", points: 0 },
    ],
  },
  // ----- Auto OEM or supplier (informational) -----
  {
    key: "cp1_auto_tier",
    cp: 1,
    label: "Auto OEM or supplier?",
    options: [
      { key: "oem", label: "OEM", flag: "OK", points: 0 },
      { key: "tier_supplier", label: "Tier 1 / Tier 2 / Tier 3 supplier", flag: "OK", points: 0 },
      { key: "not_auto", label: "Not applicable / not auto", flag: "OK", points: 0 },
    ],
  },
  // ----- KEEP: Named business driver (Need) -----
  {
    key: "cp1_named_driver",
    cp: 1,
    label: "Named business driver?",
    helpText:
      "Has the buyer named a specific business driver (margin, EBITDA, plant target, workforce cost)?",
    bantLetter: "Need",
    options: [
      { key: "named_with_mandate", label: "Named with explicit mandate", flag: "OK", points: 3 },
      { key: "named_generic", label: "Named but generic ('reduce cost')", flag: "AMBER", points: 1 },
      { key: "unknown", label: "Unknown / not surfaced", flag: "AMBER", points: 0 },
    ],
  },
  // ----- KEEP: Pain quantified (Need) -----
  {
    key: "cp1_pain_quantified",
    cp: 1,
    label: "Pain quantified?",
    bantLetter: "Need",
    options: [
      { key: "with_dollar", label: "Yes — with $ figure attached", flag: "OK", points: 3 },
      { key: "qualitative", label: "Qualitative only ('it's a lot')", flag: "AMBER", points: 1 },
      { key: "unquantified", label: "Unquantified / unknown", flag: "AMBER", points: 0 },
    ],
  },
  // ----- KEEP: Cost of inaction (Need, optional supporting) -----
  {
    key: "cp1_cost_of_inaction",
    cp: 1,
    label: "Cost of inaction stated?",
    bantLetter: "Need",
    options: [
      { key: "yes_with_number", label: "Yes — with a number", flag: "OK", points: 3 },
      { key: "yes_qualitative", label: "Yes — qualitative", flag: "AMBER", points: 1 },
      { key: "no", label: "Not stated", flag: "AMBER", points: 0 },
    ],
  },
  // ----- REMOVED 2026-05-07: L0-L5 maturity probe removed from CP1 -----
  // Reason: L0-L5 is a lead-grade awareness scale. By the time a record
  // reaches CP1 it is already an Opportunity. The M-PACT 10-checkpoint
  // engine is the maturity measurement. Replaced by incumbent-lock-in gate.
  //
  // ----- NEW: Incumbent / competitor lock-in gate -----
  {
    key: "cp1_incumbent_locked_in",
    cp: 1,
    label: "Has the prospect already signed with a competitor or locked into an incumbent vendor?",
    helpText: "A deal is lost before it starts if the buyer already committed to someone else. Verify before investing AE time.",
    softStopMessage: "Lost — incumbent locked in. Close this account and move on.",
    options: [
      { key: "no", label: "No", flag: "OK", points: 1 },
      { key: "yes", label: "Yes — they bought from someone else", flag: "SOFT_STOP", points: 0 },
    ],
  },
  // ----- KEEP: Decision driver vs. nice-to-have (soft-kill trigger) -----
  {
    key: "cp1_decision_driver_vs_nice_to_have",
    cp: 1,
    label: "Is this a decision-forcing driver or a nice-to-have?",
    helpText:
      "Captures whether the named business driver is mandatory resolution or optional improvement.",
    options: [
      {
        key: "must_resolve_with_consequence",
        label: "Buyer has stated this must be resolved — there are consequences if it isn't",
        flag: "OK",
        points: 3,
      },
      {
        key: "wants_improvement_no_consequence",
        label: "Buyer wants improvement but has not named a consequence of delay",
        flag: "AMBER",
        points: 1,
      },
      {
        key: "desirable_but_deprioritized",
        label: "Buyer characterized it as desirable but deprioritized vs. other initiatives",
        flag: "SOFT_STOP",
        points: 0,
      },
    ],
  },
  // ----- NEW: Org support (Authority) -----
  {
    key: "cp1_org_support",
    cp: 1,
    label: "Does the buyer have org support to act on this?",
    options: [
      {
        key: "exec_backing",
        label: "Yes — has executive backing / mandate already",
        flag: "OK",
        points: 3,
        // bantLetter on the option level not supported in QuestionOption,
        // so this question carries bantLetter at the question level
      },
      {
        key: "earned_through_evidence",
        label: "Earned through evidence — they need to demonstrate value/savings to leadership first",
        flag: "AMBER",
        points: 1,
      },
      {
        key: "no_support",
        label: "No support — unclear path to a decision-maker",
        flag: "AMBER",
        points: 0,
      },
    ],
    bantLetter: "Authority",
  },
  // ----- NEW: Budget signal (Budget) -----
  {
    key: "cp1_budget_signal",
    cp: 1,
    label: "Any budget signal at this stage?",
    helpText: "Light qualifier only. Deep budget gating happens later at CP7.",
    bantLetter: "Budget",
    options: [
      {
        key: "signed_allocated",
        label: "Yes — budget is signed / allocated for this",
        flag: "OK",
        points: 3,
      },
      {
        key: "needs_allocation_expected",
        label: "Needs allocation but expected",
        flag: "OK",
        points: 2,
      },
      {
        key: "authority_not_decided",
        label: "Has authority to allocate but not yet decided",
        flag: "AMBER",
        points: 1,
      },
      {
        key: "needs_approval",
        label: "No budget — has to go up one or two layers for any approval",
        flag: "AMBER",
        points: 0,
      },
      {
        key: "no_budget",
        label: "No budget at all",
        flag: "SOFT_STOP",
        points: 0,
      },
    ],
  },
  // ----- NEW: Timeline signal (Timeline) -----
  {
    key: "cp1_timeline_signal",
    cp: 1,
    label: "Is there a timeline?",
    bantLetter: "Timeline",
    options: [
      {
        key: "firm_date",
        label: "Yes — formalized firm date or specific quarter",
        flag: "OK",
        points: 3,
      },
      {
        key: "target_quarter",
        label: "Target quarter / general window stated",
        flag: "OK",
        points: 2,
      },
      {
        key: "soft_fiscal_year",
        label: "Soft / 'this fiscal year' only",
        flag: "AMBER",
        points: 1,
      },
      {
        key: "no_timeline",
        label: "No real timeline — 'whenever'",
        flag: "SOFT_STOP",
        points: 0,
      },
    ],
  },
  // ----- NEW: Authority path (Authority) -----
  {
    key: "cp1_authority_path",
    cp: 1,
    label: "Who is the buyer in the org?",
    bantLetter: "Authority",
    options: [
      {
        key: "decision_maker",
        label: "Decision-maker themselves",
        flag: "OK",
        points: 3,
      },
      {
        key: "champion_with_path",
        label: "Champion with clear path to decision-maker",
        flag: "OK",
        points: 2,
      },
      {
        key: "influencer_only",
        label: "Influencer only — no clear path",
        flag: "AMBER",
        points: 1,
      },
      {
        key: "unknown",
        label: "Unknown",
        flag: "AMBER",
        points: 0,
      },
    ],
  },
];

// =====================================================================
// CP 2 — Stakeholder Map
// Live: 9 questions (max 25 pts)
// New: 2 questions (expansion proposal)
// =====================================================================
const CP2_QUESTIONS: QuestionDef[] = [
  // ----- LIVE (9 questions) -----
  {
    key: "cp2_finance_coverage",
    cp: 2,
    label: "Finance buyer covered? (CFO / VP Fin / Comptroller)",
    options: [
      { key: "confirmed", label: "Confirmed — multi-thread", flag: "OK", points: 3 },
      { key: "single", label: "Single contact", flag: "AMBER", points: 1 },
      { key: "unknown", label: "Unknown / no contact", flag: "AMBER", points: 0 },
    ],
  },
  {
    key: "cp2_ops_coverage",
    cp: 2,
    label: "Operations buyer covered? (President / COO / VP Ops)",
    options: [
      { key: "confirmed", label: "Confirmed — multi-thread", flag: "OK", points: 3 },
      { key: "single", label: "Single contact", flag: "AMBER", points: 1 },
      { key: "unknown", label: "Unknown / no contact", flag: "AMBER", points: 0 },
    ],
  },
  {
    key: "cp2_hr_coverage",
    cp: 2,
    label: "Have you confirmed at least one HR/People Ops contact in the buying coalition?",
    options: [
      { key: "confirmed", label: "Confirmed", flag: "OK", points: 2 },
      { key: "single", label: "Single contact", flag: "AMBER", points: 1 },
      { key: "unknown", label: "Unknown / no contact", flag: "AMBER", points: 0 },
    ],
  },
  {
    key: "cp2_champion",
    cp: 2,
    label: "Champion identified?",
    helpText: "Champion = named, coached, repeats our message back.",
    options: [
      {
        key: "named_coached",
        label: "Named, coached, repeats our message",
        flag: "OK",
        points: 3,
      },
      { key: "named_only", label: "Named only — not yet coached", flag: "AMBER", points: 1 },
      { key: "none", label: "No champion (SOFT STOP)", flag: "SOFT_STOP", points: 0 },
    ],
  },
  {
    key: "cp2_economic_buyer",
    cp: 2,
    label: "Economic buyer identified?",
    options: [
      { key: "named_veto", label: "Named — has veto power", flag: "OK", points: 3 },
      { key: "suspected", label: "Suspected — not confirmed", flag: "AMBER", points: 1 },
      { key: "unknown", label: "Unknown (SOFT STOP)", flag: "SOFT_STOP", points: 0 },
    ],
  },
  {
    key: "cp2_fox",
    cp: 2,
    label: "Informal power broker identified?",
    options: [
      { key: "yes", label: "Yes — named", flag: "OK", points: 2 },
      { key: "suspected", label: "Suspected", flag: "AMBER", points: 1 },
      { key: "unknown", label: "Unknown", flag: "AMBER", points: 0 },
    ],
  },
  {
    key: "cp2_detractors",
    cp: 2,
    label: "Detractors mapped?",
    options: [
      {
        key: "named_with_plan",
        label: "Named — with mitigation plan",
        flag: "OK",
        points: 3,
      },
      {
        key: "named_no_plan",
        label: "Named — no mitigation plan",
        flag: "AMBER",
        points: 1,
      },
      { key: "unknown", label: "Unknown", flag: "AMBER", points: 0 },
    ],
  },
  {
    key: "cp2_multi_thread",
    cp: 2,
    label: "Multi-thread coverage?",
    options: [
      { key: "three_plus", label: "3+ stakeholders engaged", flag: "OK", points: 3 },
      { key: "one_two", label: "1–2 stakeholders", flag: "AMBER", points: 1 },
      { key: "single_thread", label: "Single thread", flag: "AMBER", points: 0 },
    ],
  },
  {
    key: "cp2_exec_sponsor_level",
    cp: 2,
    label: "Executive sponsor level?",
    options: [
      { key: "csuite", label: "C-suite", flag: "OK", points: 3 },
      { key: "vp", label: "VP", flag: "OK", points: 2 },
      {
        key: "director_below",
        label: "Director or below (SOFT STOP)",
        flag: "SOFT_STOP",
        points: 0,
      },
    ],
  },

  // ----- NEW from expansion proposal (2 questions) -----
  {
    key: "cp2_champion_internal_risk_taken",
    cp: 2,
    label: "Has the champion advocated without us in the room?",
    helpText:
      "The critical distinction between a passive supporter and a real champion. A champion who has never presented internally without us present has not put political capital on the line.",
    options: [
      {
        key: "yes_outcome_positive",
        label: "Yes — champion has presented or briefed internal stakeholders without us; outcome positive",
        flag: "OK",
        points: 3,
      },
      {
        key: "yes_outcome_unknown",
        label: "Yes — champion has advocated internally but outcome is unknown or mixed",
        flag: "AMBER",
        points: 1,
      },
      {
        key: "no_not_yet",
        label: "No — champion has not yet presented or advocated without our presence",
        flag: "SOFT_STOP",
        points: 0,
      },
    ],
  },
  {
    key: "cp2_economic_buyer_direct_contact",
    cp: 2,
    label: "Economic buyer spoken to us directly?",
    helpText:
      "cp2_economic_buyer scores whether the EB is identified. This scores whether we have actually spoken to them.",
    options: [
      {
        key: "yes_direct_conversation",
        label: "Yes — we have had at least one direct conversation with the economic buyer",
        flag: "OK",
        points: 3,
      },
      {
        key: "intro_no_substance",
        label: "Intro made; no substantive conversation yet",
        flag: "AMBER",
        points: 1,
      },
      {
        key: "no_direct_contact",
        label: "No direct contact — all EB knowledge is from the champion's account",
        flag: "SOFT_STOP",
        points: 0,
      },
    ],
  },
  {
    key: "cp2_competitive_ownership_conflict",
    cp: 2,
    label: "Competitive ownership conflict?",
    helpText:
      "Is the account owned by, a subsidiary of, or otherwise legally bound to a direct competitor of Mi6 / Martinrea? Discovered competitive ownership is a hard kill — walk away. Examples: Magna subsidiary, in-flight acquisition by a competitor parent.",
    options: [
      {
        key: "clean",
        label: "Clean — confirmed independent of any direct competitor",
        flag: "OK",
        points: 3,
      },
      {
        key: "unverified",
        label: "Not yet verified — ownership chain not walked",
        flag: "AMBER",
        points: 0,
      },
      {
        key: "adjacent_relationship",
        label: "Adjacent relationship (shared customer, partnership) — disclosable, manageable",
        flag: "AMBER",
        points: 0,
      },
      {
        key: "competitor_subsidiary",
        label: "Owned by or subsidiary of a direct competitor (e.g., Magna)",
        flag: "HARD_STOP",
        points: 0,
      },
      {
        key: "competitor_acquisition_inflight",
        label: "Active acquisition in-flight by a direct competitor",
        flag: "HARD_STOP",
        points: 0,
      },
    ],
  },
];

// =====================================================================
// CP 3 — Problem Depth
// Live: 6 questions (max 20 pts)
// New: 3 questions (expansion proposal)
// =====================================================================
const CP3_QUESTIONS: QuestionDef[] = [
  // ----- LIVE (6 questions) -----
  {
    key: "cp3_ci_culture",
    cp: 3,
    label: "Culture of continuous improvement?",
    options: [
      { key: "programmatic", label: "Programmatic — they live it", flag: "OK", points: 3 },
      {
        key: "aspirational",
        label: "Aspirational — talked about, not lived",
        flag: "AMBER",
        points: 1,
      },
      { key: "absent", label: "Absent", flag: "AMBER", points: 0 },
    ],
  },
  {
    key: "cp3_implementation_followthrough",
    cp: 3,
    label: "Implementation follow-through problem stated?",
    options: [
      {
        key: "yes_specific",
        label: "Yes — specific examples cited",
        flag: "OK",
        points: 4,
      },
      {
        key: "yes_general",
        label: "Yes — general 'things slip'",
        flag: "AMBER",
        points: 2,
      },
      { key: "no", label: "Not stated", flag: "AMBER", points: 0 },
    ],
  },
  {
    key: "cp3_accountability_gap",
    cp: 3,
    label: "Enterprise accountability gap identified?",
    options: [
      { key: "named_gap", label: "Named gap", flag: "OK", points: 3 },
      { key: "vague", label: "Vague references", flag: "AMBER", points: 1 },
      { key: "no", label: "Not identified", flag: "AMBER", points: 0 },
    ],
  },
  {
    key: "cp3_scorecard_gap",
    cp: 3,
    label: "Scorecard / dashboard gap?",
    options: [
      {
        key: "excel_manual",
        label: "Excel / manual today (best opportunity)",
        flag: "OK",
        points: 4,
      },
      { key: "partial", label: "Partial dashboards", flag: "AMBER", points: 2 },
      { key: "mature", label: "Mature dashboards already", flag: "AMBER", points: 0 },
    ],
  },
  {
    key: "cp3_alignment",
    cp: 3,
    label: "Alignment on goals & priorities?",
    options: [
      {
        key: "misaligned_acknowledged",
        label: "Misaligned + buyer acknowledges",
        flag: "OK",
        points: 3,
      },
      { key: "partial", label: "Partial alignment", flag: "AMBER", points: 1 },
      { key: "aligned_no_pain", label: "Already aligned — no pain", flag: "AMBER", points: 0 },
    ],
  },
  {
    key: "cp3_realtime_mobile",
    cp: 3,
    label: "Real-time data + mobile access need?",
    options: [
      { key: "yes_named", label: "Yes — explicitly named", flag: "OK", points: 3 },
      { key: "mentioned", label: "Mentioned in passing", flag: "AMBER", points: 1 },
      { key: "no", label: "Not raised", flag: "AMBER", points: 0 },
    ],
  },

  // ----- NEW from expansion proposal (3 questions) -----
  {
    key: "cp3_pain_owner_named",
    cp: 3,
    label: "Is there a named internal owner of this problem?",
    helpText:
      "Pain without an owner is a complaint, not a business problem. Tests whether the pain maps to a person whose role and performance are tied to solving it.",
    options: [
      {
        key: "named_formal_performance",
        label:
          "Named individual — it is their stated responsibility to solve this; tied to their performance",
        flag: "OK",
        points: 3,
      },
      {
        key: "named_informal",
        label: "Named individual — owns the problem informally; not a formal performance metric",
        flag: "AMBER",
        points: 1,
      },
      {
        key: "no_owner",
        label: "No named owner — pain acknowledged but no one is accountable for resolving it",
        // TODO for Bryan: Upgrade this to SOFT_STOP? See open-questions.md OQ1.
        flag: "AMBER",
        points: 0,
      },
    ],
  },
  {
    key: "cp3_twelve_month_consequence",
    cp: 3,
    label: "Consequence if this problem is unchanged in 12 months?",
    helpText:
      "Forces the buyer to articulate a future-state cost of inaction anchored to a time horizon. If they cannot name a consequence, the pain is aspirational.",
    options: [
      {
        key: "named_with_metric",
        label: "Named — specific business consequence with a dollar or operational metric attached",
        flag: "OK",
        points: 4,
      },
      {
        key: "named_directional",
        label: "Named — directional consequence without a specific number ('we'll keep losing ground')",
        flag: "AMBER",
        points: 2,
      },
      {
        key: "no_consequence_named",
        label: "No consequence named — buyer cannot or will not articulate what happens if nothing changes",
        flag: "SOFT_STOP",
        points: 0,
      },
    ],
  },
  {
    key: "cp3_prior_attempt_to_solve",
    cp: 3,
    label: "Has this organization tried to solve this problem before?",
    helpText:
      "Prior failed initiatives are the single strongest predictor of adoption and change management risk. Also validates depth of pain.",
    options: [
      {
        key: "yes_failed_with_diagnosis",
        label: "Yes — tried before and failed; buyer articulates why and what they need differently this time",
        flag: "OK",
        points: 3,
      },
      {
        key: "yes_failed_no_diagnosis",
        label: "Yes — tried before; no clear diagnosis of why it did not work",
        flag: "AMBER",
        points: 1,
      },
      {
        key: "no_first_attempt",
        label: "No — this is the first attempt to address it",
        flag: "AMBER",
        points: 2,
      },
      {
        key: "unknown",
        label: "Unknown — buyer has not discussed prior attempts",
        flag: "AMBER",
        points: 0,
      },
    ],
  },
];

// =====================================================================
// CP 4 — Organizational Readiness
// Live: 4 questions (max 15 pts)
// New: 2 questions (expansion proposal)
// =====================================================================
const CP4_QUESTIONS: QuestionDef[] = [
  // ----- LIVE (4 questions) -----
  {
    key: "cp4_exec_sponsorship",
    cp: 4,
    label: "Executive sponsorship commitment?",
    options: [
      {
        key: "signed",
        label: "Signed / written commitment from exec",
        flag: "OK",
        points: 4,
      },
      { key: "verbal", label: "Verbal only", flag: "AMBER", points: 2 },
      { key: "none", label: "None", flag: "AMBER", points: 0 },
    ],
  },
  {
    key: "cp4_change_mgmt",
    cp: 4,
    label: "Change management readiness?",
    options: [
      { key: "resourced", label: "Resourced — owner + budget", flag: "OK", points: 4 },
      {
        key: "planned",
        label: "Planned but not resourced",
        flag: "AMBER",
        points: 2,
      },
      { key: "unaddressed", label: "Unaddressed", flag: "AMBER", points: 0 },
    ],
  },
  {
    key: "cp4_adoption_plan",
    cp: 4,
    label: "User adoption plan?",
    options: [
      { key: "owner_and_plan", label: "Named owner + plan", flag: "OK", points: 3 },
      { key: "owner_only", label: "Named owner only", flag: "AMBER", points: 1 },
      { key: "none", label: "None", flag: "AMBER", points: 0 },
    ],
  },

  // ----- NEW from expansion proposal (2 questions) -----
  {
    key: "cp4_prior_initiative_failure",
    cp: 4,
    label: "Has a similar change initiative stalled or failed at this organization?",
    helpText:
      "Directly predicts adoption risk. An organization that has tried to install an operating system, reporting rhythm, or behavioral change program before and failed is a higher-risk implementation.",
    options: [
      {
        key: "no_known_failure",
        label: "No known prior failure in this category",
        flag: "OK",
        points: 2,
      },
      {
        key: "prior_stalled_addressed",
        label: "Prior initiative attempted; stalled for reasons that have since been addressed",
        flag: "AMBER",
        points: 1,
      },
      {
        key: "prior_failed_unaddressed",
        label: "Prior initiative failed; buyer acknowledges the failure but has not explained what is different this time",
        flag: "SOFT_STOP",
        points: 0,
      },
      {
        key: "prior_failed_unacknowledged",
        label: "Prior initiative failed; buyer is not aware of or has not acknowledged the failure",
        flag: "SOFT_STOP",
        points: 0,
      },
    ],
  },
  {
    key: "cp4_training_owner",
    cp: 4,
    label: "Named training and onboarding owner?",
    helpText:
      "Change management commitment and training execution are often different owners, different budgets, and different timelines.",
    options: [
      {
        key: "named_with_budget_plan",
        label: "Named training owner with budget and a rollout plan",
        flag: "OK",
        points: 3,
      },
      {
        key: "named_no_budget",
        label: "Named training owner — no budget or plan yet",
        flag: "AMBER",
        points: 1,
      },
      {
        key: "informal_no_owner",
        label: "Training deferred to champion informally — no owner, no plan",
        flag: "AMBER",
        points: 0,
      },
      { key: "not_discussed", label: "Not discussed", flag: "AMBER", points: 0 },
    ],
  },
];

// =====================================================================
// CP 5 — Technical Fit
// Live: 4 questions (max 10 pts)
// New: 2 questions (expansion proposal)
// =====================================================================
const CP5_QUESTIONS: QuestionDef[] = [
  // ----- LIVE (4 questions) -----
  {
    key: "cp5_cloud_readiness",
    cp: 5,
    label: "Cloud readiness?",
    options: [
      { key: "cloud_ok", label: "Cloud OK — already on cloud", flag: "OK", points: 3 },
      { key: "hybrid", label: "Hybrid", flag: "OK", points: 2 },
      { key: "on_prem_only", label: "On-prem only", flag: "AMBER", points: 1 },
      {
        key: "cloud_banned",
        label: "Cloud banned (HARD STOP)",
        flag: "HARD_STOP",
        points: 0,
      },
    ],
  },
  {
    key: "cp5_erp_integration",
    cp: 5,
    label: "ERP / financial system integration confirmed?",
    options: [
      {
        key: "specified",
        label: "Specified — system + version known",
        flag: "OK",
        points: 3,
      },
      { key: "unknown", label: "Unknown", flag: "AMBER", points: 1 },
      { key: "blocked", label: "Blocked by IT policy", flag: "AMBER", points: 0 },
    ],
  },
  {
    key: "cp5_it_engaged",
    cp: 5,
    label: "IT stakeholder engaged?",
    options: [
      { key: "named", label: "Yes — named contact", flag: "OK", points: 2 },
      { key: "loose", label: "Loose engagement", flag: "AMBER", points: 1 },
      { key: "no", label: "No", flag: "AMBER", points: 0 },
    ],
  },
  {
    key: "cp5_data_quality",
    cp: 5,
    label: "Data environment quality?",
    options: [
      { key: "clean", label: "Clean — well-governed", flag: "OK", points: 2 },
      { key: "mixed", label: "Mixed", flag: "AMBER", points: 1 },
      { key: "chaotic", label: "Chaotic", flag: "AMBER", points: 0 },
    ],
  },

  // ----- NEW from expansion proposal (2 questions) -----
  {
    key: "cp5_integration_timeline",
    cp: 5,
    label: "ERP integration — scheduled and owned?",
    helpText:
      "cp5_erp_integration confirms the system and version are known. This confirms whether the integration work is on someone's project plan with a date.",
    options: [
      {
        key: "scheduled_owned",
        label: "Scheduled — named IT owner, timeline agreed, in the project plan",
        flag: "OK",
        points: 3,
      },
      {
        key: "owner_no_timeline",
        label: "Named IT owner — timeline not yet set",
        flag: "AMBER",
        points: 1,
      },
      {
        key: "no_owner_unknown",
        label: "IT owner not named; integration timeline unknown",
        flag: "AMBER",
        points: 0,
      },
      {
        key: "no_integration_required",
        label: "No integration required — system is standalone",
        flag: "OK",
        points: 3,
      },
    ],
  },
  {
    key: "cp5_security_review_status",
    cp: 5,
    label: "Security / infosec review initiated?",
    helpText:
      "The v3 scoring spec allocates 14% composite weight to IT/Security, but this dimension has no representation in the live tree. This adds a lightweight gate.",
    // TODO for Bryan: Should this become a dedicated CP5b (Security / IT Review) gate?
    // See aip-debug/bank-open-questions.md OQ4.
    options: [
      {
        key: "completed_clean",
        label: "Completed — no findings or findings resolved",
        flag: "OK",
        points: 2,
      },
      {
        key: "in_progress",
        label: "In progress — review underway, no blockers identified yet",
        flag: "AMBER",
        points: 1,
      },
      {
        key: "required_not_started",
        label: "Required but not yet started",
        flag: "AMBER",
        points: 0,
      },
      {
        key: "not_required",
        label: "Not required for this deal",
        flag: "OK",
        points: 2,
      },
      {
        key: "required_known_blocker",
        label: "Required; known issue that has not been resolved",
        flag: "HARD_STOP",
        points: 0,
      },
    ],
  },
];

// =====================================================================
// CP 6 — Demonstration (NEW — added 2026-05-06 per Bryan)
// Hard-kill is CONDITIONAL: only triggers when buyer reports a must-have
// requirement got WORSE after the demo (we identified a gap on something
// they need). "No demo at all" + must-haves listed = hard-kill.
// Series of soft stops here compound into amber/red.
// 12 questions, max 30 pts.
// =====================================================================
const CP6_QUESTIONS: QuestionDef[] = [
  // ----- Pre-demo posture -----
  {
    key: "cp6_real_demo_run",
    cp: 6,
    label: "Was a real demo actually run?",
    helpText:
      "A real demo means a working product (or production-fidelity walkthrough) was shown to the buyer's people — not slides, not a recording, not a sales pitch.",
    options: [
      { key: "live_product", label: "Yes — live product walked through with the buyer", flag: "OK", points: 4 },
      { key: "recorded_walkthrough", label: "Recorded walkthrough or scripted video shown", flag: "AMBER", points: 1 },
      { key: "slides_only", label: "Slides / pitch only — no product shown", flag: "SOFT_STOP", points: 0 },
      { key: "not_run_yet", label: "Not run yet — demo still ahead", flag: "AMBER", points: 0 },
      { key: "not_run_refused", label: "Buyer refused / cancelled the demo", flag: "HARD_STOP", points: 0 },
    ],
  },
  {
    key: "cp6_must_have_list",
    cp: 6,
    label: "Pre-demo must-have list captured?",
    helpText:
      "Before the demo, did we capture each requirement the buyer cares about and tag it must / nice / not needed? Without this list you cannot judge whether the demo improved the situation.",
    options: [
      { key: "yes_signed_off", label: "Yes — list captured and buyer signed off on the must-haves", flag: "OK", points: 4 },
      { key: "yes_internal", label: "Yes — we built the list internally (not confirmed with buyer)", flag: "AMBER", points: 2 },
      { key: "partial", label: "Partial — some requirements listed, must / nice not tagged", flag: "AMBER", points: 1 },
      { key: "none", label: "No list — we walked in and showed product", flag: "SOFT_STOP", points: 0 },
    ],
  },
  {
    key: "cp6_must_have_count",
    cp: 6,
    label: "How many must-have requirements are on the list?",
    helpText: "Count only items the buyer tagged 'must' — nice-to-haves and not-needed do not count toward this number.",
    options: [
      { key: "three_to_six", label: "3 to 6 must-haves (healthy)", flag: "OK", points: 2 },
      { key: "one_or_two", label: "1 or 2 must-haves (thin — are we in early scoping?)", flag: "AMBER", points: 1 },
      { key: "seven_to_twelve", label: "7 to 12 must-haves (heavy — increases gap risk)", flag: "AMBER", points: 1 },
      { key: "thirteen_plus", label: "13 or more must-haves (likely RFP — break into phases)", flag: "AMBER", points: 0 },
      { key: "none_listed", label: "None listed yet", flag: "SOFT_STOP", points: 0 },
    ],
  },

  // ----- Demo execution -----
  {
    key: "cp6_right_stakeholders",
    cp: 6,
    label: "Were the right stakeholders in the demo?",
    helpText:
      "At minimum: economic buyer (or their delegate), the user-side owner, and IT/Ops if technical fit matters. Missing the EB makes the verdict non-binding.",
    options: [
      { key: "eb_user_it", label: "EB + user owner + IT/Ops all attended", flag: "OK", points: 3 },
      { key: "user_it_no_eb", label: "User + IT but no economic buyer", flag: "AMBER", points: 1 },
      { key: "user_only", label: "User-side only (no EB, no IT)", flag: "AMBER", points: 1 },
      { key: "wrong_room", label: "Wrong room — only middle managers / no decision authority present", flag: "SOFT_STOP", points: 0 },
    ],
  },
  {
    key: "cp6_scripted_to_use_case",
    cp: 6,
    label: "Was the demo scripted to their actual use case?",
    helpText:
      "Did we walk their workflow, their data shape, their roles — or did we show our standard demo path?",
    options: [
      { key: "their_workflow", label: "Yes — their workflow, their named roles, their data fields", flag: "OK", points: 3 },
      { key: "hybrid", label: "Hybrid — some of theirs, mostly ours", flag: "AMBER", points: 1 },
      { key: "standard_demo", label: "Standard demo path — not customized", flag: "AMBER", points: 0 },
    ],
  },

  // ----- In-demo confirmation (rolled-up across requirements) -----
  {
    key: "cp6_must_have_confirmed",
    cp: 6,
    label: "Of the must-haves, how many were confirmed in the demo?",
    helpText:
      "Confirmed = the buyer saw it working and acknowledged it meets the requirement. 'Promised' or 'roadmap' does not count as confirmed.",
    options: [
      { key: "all", label: "All must-haves confirmed (yes — it does that)", flag: "OK", points: 5 },
      { key: "all_but_one_partial", label: "All confirmed except one partial (close but flagged)", flag: "AMBER", points: 3 },
      { key: "some_no_or_partial", label: "Some confirmed, others partial or no", flag: "AMBER", points: 1 },
      { key: "most_no_or_partial", label: "Most must-haves came back partial or no", flag: "SOFT_STOP", points: 0 },
      { key: "none_run", label: "Demo did not cover the must-haves", flag: "SOFT_STOP", points: 0 },
    ],
  },
  {
    key: "cp6_open_gaps_count",
    cp: 6,
    label: "How many must-have gaps are still open after the demo?",
    helpText:
      "A gap = a must-have we cannot demonstrate today (no, partial, or 'on roadmap'). N/A items do not count.",
    options: [
      { key: "zero", label: "Zero open gaps on must-haves", flag: "OK", points: 3 },
      { key: "one", label: "1 open gap (we have a credible path to close)", flag: "AMBER", points: 2 },
      { key: "one_no_path", label: "1 open gap (no credible path within their timeline)", flag: "SOFT_STOP", points: 0 },
      { key: "two_plus", label: "2 or more open gaps on must-haves", flag: "SOFT_STOP", points: 0 },
    ],
  },

  // ----- Post-demo verdict (Bryan's framing) -----
  {
    key: "cp6_verdict",
    cp: 6,
    label: "Post-demo verdict — did this improve our situation?",
    helpText:
      "Bryan's frame: did the demo (a) improve our position, (b) leave us where we were, or (c) make it worse because we surfaced things we don't do that they need? 'Worse — gap on a must-have' is the hard-stop trigger.",
    options: [
      { key: "improved", label: "Improved — buyer saw fit, advocate strengthened", flag: "OK", points: 4 },
      { key: "same", label: "Same — no movement, no new objections", flag: "AMBER", points: 1 },
      { key: "worse_nice", label: "Worse — surfaced gaps but only on nice-to-haves", flag: "AMBER", points: 0 },
      { key: "worse_must_have", label: "Worse — surfaced a gap on a MUST-HAVE we cannot close", flag: "HARD_STOP", points: 0 },
    ],
  },
  {
    key: "cp6_buyer_next_step",
    cp: 6,
    label: "Did the buyer name the next step coming out of the demo?",
    helpText:
      "Coachable signal: a real buyer leaves a successful demo with the next step on their lips (security review, pricing, reference call, scope proposal). Silence after a demo means it did not move them.",
    options: [
      { key: "specific_next_step", label: "Yes — they named a specific next step with owner and date", flag: "OK", points: 3 },
      { key: "general_interest", label: "General interest — ‘let us think on it / circle back’", flag: "AMBER", points: 1 },
      { key: "no_signal", label: "No clear signal — demo ended without commitment", flag: "SOFT_STOP", points: 0 },
    ],
  },
  {
    key: "cp6_followup_doc",
    cp: 6,
    label: "Was the demo follow-up captured in writing?",
    helpText:
      "Confirmed must-haves, open gaps, and the named next step — sent back to the buyer within 48 hours. This is what makes the verdict binding.",
    options: [
      { key: "sent_acknowledged", label: "Sent and buyer acknowledged the recap", flag: "OK", points: 2 },
      { key: "sent_no_ack", label: "Sent, no acknowledgment yet", flag: "AMBER", points: 1 },
      { key: "not_sent", label: "Not sent — we have not closed the loop", flag: "AMBER", points: 0 },
    ],
  },

  // ----- Disqualifier checks (single-answer hard-stop possible) -----
  {
    key: "cp6_capability_misrepresented",
    cp: 6,
    label: "Was any capability misrepresented or oversold in the demo?",
    helpText:
      "If the team showed a capability as live when it is on roadmap, or fudged a workflow, that is a recoverable error — but only if we correct it in writing immediately. Uncaught misrepresentation kills the deal at legal.",
    options: [
      { key: "clean", label: "Clean — no overselling, no roadmap-as-live", flag: "OK", points: 1 },
      { key: "corrected", label: "Corrected in follow-up — buyer informed, no surprise at legal", flag: "AMBER", points: 1 },
      { key: "uncorrected", label: "Uncorrected misrepresentation on a must-have", flag: "HARD_STOP", points: 0 },
    ],
  },
  {
    key: "cp6_buyer_engagement",
    cp: 6,
    label: "Buyer engagement during the demo?",
    helpText:
      "Were they asking questions, mapping it onto their team / process — or watching passively? Disengagement is a signal, not a courtesy.",
    options: [
      { key: "high", label: "High — specific questions, named team members, asked for variants", flag: "OK", points: 2 },
      { key: "medium", label: "Medium — polite questions, no mapping to their world", flag: "AMBER", points: 1 },
      { key: "low", label: "Low — watched passively, no questions", flag: "SOFT_STOP", points: 0 },
    ],
  },
];

// =====================================================================
// CP 7 — Budget / Commercial
// Live: 7 questions (max 20 pts)
// New: 2 questions (expansion proposal)
// =====================================================================
const CP7_QUESTIONS: QuestionDef[] = [
  // ----- LIVE (7 questions) -----
  {
    key: "cp7_budget_confirmed",
    cp: 7,
    label: "Budget confirmed?",
    options: [
      {
        key: "named_line",
        label: "Named line item / approved",
        flag: "OK",
        points: 4,
      },
      {
        key: "general",
        label: "General budget — not earmarked",
        flag: "AMBER",
        points: 2,
      },
      { key: "unfunded", label: "Unfunded (SOFT STOP)", flag: "SOFT_STOP", points: 0 },
    ],
  },
  {
    key: "cp7_capex_opex",
    cp: 7,
    label: "CapEx vs. OpEx classification?",
    options: [
      { key: "capex", label: "CapEx", flag: "OK", points: 2 },
      { key: "opex", label: "OpEx", flag: "OK", points: 2 },
      { key: "unknown", label: "Unknown", flag: "AMBER", points: 0 },
    ],
  },
  {
    key: "cp7_within_range",
    cp: 7,
    label: "Price within budget range?",
    options: [
      { key: "within", label: "Within range", flag: "OK", points: 3 },
      { key: "stretch", label: "Stretch", flag: "AMBER", points: 1 },
      { key: "out", label: "Out of range", flag: "AMBER", points: 0 },
    ],
  },
  {
    key: "cp7_procurement_path",
    cp: 7,
    label: "Procurement / approval path?",
    options: [
      { key: "mapped", label: "Mapped — every step known", flag: "OK", points: 3 },
      { key: "partial", label: "Partial mapping", flag: "AMBER", points: 1 },
      { key: "unknown", label: "Unknown", flag: "AMBER", points: 0 },
    ],
  },
  {
    key: "cp7_legal_msa",
    cp: 7,
    label: "Legal / MSA status?",
    options: [
      { key: "executed", label: "MSA executed", flag: "OK", points: 2 },
      { key: "drafted", label: "Drafted — under review", flag: "AMBER", points: 1 },
      { key: "not_started", label: "Not started", flag: "AMBER", points: 0 },
    ],
  },
  {
    key: "cp7_cost_inaction_anchor",
    cp: 7,
    label: "Cost-of-inaction anchor set?",
    options: [
      {
        key: "yes_with_number",
        label: "Yes — anchored with a number",
        flag: "OK",
        points: 3,
      },
      {
        key: "qualitative",
        label: "Qualitative anchor",
        flag: "AMBER",
        points: 1,
      },
      {
        key: "no",
        label: "No anchor (SOFT STOP)",
        flag: "SOFT_STOP",
        points: 0,
      },
    ],
  },
  {
    key: "cp7_roi_case",
    cp: 7,
    label: "ROI case built?",
    options: [
      { key: "signed_off", label: "Signed off by buyer", flag: "OK", points: 3 },
      { key: "drafted", label: "Drafted but unsigned", flag: "AMBER", points: 1 },
      { key: "none", label: "None (SOFT STOP)", flag: "SOFT_STOP", points: 0 },
    ],
  },

  // ----- NEW from expansion proposal (2 questions) -----
  {
    key: "cp7_budget_period",
    cp: 7,
    label: "Budget in-period or future-year?",
    helpText:
      "A named budget line item in next fiscal year carries fundamentally different forecast weight than an approved current-period spend.",
    options: [
      {
        key: "in_period",
        label: "In-period — approved and available in the current quarter",
        flag: "OK",
        points: 3,
      },
      {
        key: "next_quarter",
        label: "Next quarter — budget approved for Q+1; champion is ready to act",
        flag: "AMBER",
        points: 1,
      },
      {
        key: "next_fiscal_year",
        label: "Next fiscal year — budget exists in plan but is not yet accessible",
        flag: "SOFT_STOP",
        points: 0,
      },
      {
        key: "unknown",
        label: "Unknown — rep has not confirmed when the budget is available",
        flag: "AMBER",
        points: 0,
      },
    ],
  },
  {
    key: "cp7_eb_spend_authority",
    cp: 7,
    label: "Economic buyer's unilateral spend authority confirmed?",
    helpText:
      "Whether budget exists and whether the EB can approve it without committee sign-off are different questions.",
    options: [
      {
        key: "unilateral_confirmed",
        label: "EB has confirmed unilateral authority to approve this spend",
        flag: "OK",
        points: 3,
      },
      {
        key: "one_more_approval",
        label: "EB requires one additional approval (CFO or equivalent) — known and in plan",
        flag: "AMBER",
        points: 1,
      },
      {
        key: "committee_required",
        label: "EB requires committee or board approval — not yet mapped",
        flag: "AMBER",
        points: 0,
      },
      {
        key: "unknown",
        label: "Unknown — rep has not confirmed the EB's approval authority",
        flag: "AMBER",
        points: 0,
      },
    ],
  },
  {
    key: "cp7_financial_viability",
    cp: 7,
    label: "Financial viability of the buyer?",
    helpText:
      "Can the account legally and operationally pay? Bankruptcy filing, court-ordered freeze on spend, or insolvency are hard kills — procurement is frozen and no commercial path exists. Distress short of bankruptcy is a soft stop pending finance review.",
    options: [
      {
        key: "strong",
        label: "Strong — public financials or recent funding confirm capacity to pay",
        flag: "OK",
        points: 3,
      },
      {
        key: "adequate",
        label: "Adequate — no red flags surfaced; standard credit posture",
        flag: "OK",
        points: 2,
      },
      {
        key: "unverified",
        label: "Unverified — finance partner has not pulled credit / D&B",
        flag: "AMBER",
        points: 0,
      },
      {
        key: "distressed",
        label: "Distressed — covenant breach, layoffs, parent pressure (not yet in bankruptcy)",
        flag: "SOFT_STOP",
        points: 0,
      },
      {
        key: "bankruptcy_filed",
        label: "Bankruptcy filed / Chapter 11 / receivership — spend frozen",
        flag: "HARD_STOP",
        points: 0,
      },
      {
        key: "insolvent",
        label: "Insolvent or court-ordered freeze on commercial commitments",
        flag: "HARD_STOP",
        points: 0,
      },
    ],
  },
];

// =====================================================================
// CP 8 — Timing & Forecast
// Live: 7 questions (max 15 pts)
// New: 2 questions (expansion proposal)
// =====================================================================
const CP8_QUESTIONS: QuestionDef[] = [
  // ----- LIVE (7 questions) -----
  {
    key: "cp8_close_date",
    cp: 8,
    label: "Mutual close date?",
    options: [
      {
        key: "joint_signed",
        label: "Joint mutual close date in writing",
        flag: "OK",
        points: 3,
      },
      {
        key: "our_date",
        label: "Our date — buyer hasn't agreed",
        flag: "AMBER",
        points: 1,
      },
      { key: "unknown", label: "Unknown", flag: "AMBER", points: 0 },
    ],
  },
  {
    key: "cp8_compelling_event",
    cp: 8,
    label: "Firm or formal timeline?",
    helpText:
      "Default is a firm or formal date the buyer has set for this project — official timeline, can still slide. A true formal timeline is the rare case where an external, dated event forces the decision with significant consequences. Formal timelines cannot be manufactured.",
    options: [
      {
        key: "compelling_event",
        label: "Formal timeline — external dated event with significant consequence if missed",
        flag: "OK",
        points: 3,
      },
      {
        key: "firm_formal",
        label: "Firm / formal timeline — buyer has set an official project date",
        flag: "OK",
        points: 3,
      },
      {
        key: "target_only",
        label: "Target date — buyer's preference, not yet formalized",
        flag: "AMBER",
        points: 1,
      },
      {
        key: "none",
        label: "No firm or formal date — close date driven by rep's forecast",
        flag: "SOFT_STOP",
        points: 0,
      },
    ],
  },
  {
    key: "cp8_map",
    cp: 8,
    label: "Mutual Action Plan (MAP)?",
    options: [
      { key: "signed", label: "Signed by buyer", flag: "OK", points: 3 },
      { key: "drafted", label: "Drafted — not signed", flag: "AMBER", points: 1 },
      { key: "none", label: "None", flag: "AMBER", points: 0 },
    ],
  },
  {
    key: "cp8_next_steps",
    cp: 8,
    label: "Next steps booked?",
    options: [
      {
        key: "calendar",
        label: "On calendar with invite accepted",
        flag: "OK",
        points: 2,
      },
      { key: "verbal", label: "Verbal only", flag: "AMBER", points: 1 },
      { key: "none", label: "None", flag: "AMBER", points: 0 },
    ],
  },
  {
    key: "cp8_customer_resources",
    cp: 8,
    label: "Customer resources committed?",
    options: [
      {
        key: "named",
        label: "Yes — named owners on their side",
        flag: "OK",
        points: 2,
      },
      {
        key: "partial",
        label: "Partial commitment",
        flag: "AMBER",
        points: 1,
      },
      { key: "none", label: "None", flag: "AMBER", points: 0 },
    ],
  },
  {
    key: "cp8_rfp_branch",
    cp: 8,
    label: "RFP branch — if RFP, did we shape it?",
    options: [
      { key: "shaped", label: "We shaped the RFP", flag: "OK", points: 1 },
      {
        key: "not_shaped",
        label: "RFP — we did not shape",
        flag: "AMBER",
        points: 0,
      },
      { key: "not_rfp", label: "Not an RFP", flag: "OK", points: 1 },
    ],
  },
  // ----- NEW from expansion proposal (2 questions) -----
  {
    key: "cp8_ce_corroboration",
    cp: 8,
    label: "Timeline confirmed by someone other than the champion?",
    helpText:
      "cp8_compelling_event scores whether a firm/formal date (or rare formal timeline) exists. This scores whether the date has been corroborated by a second party.",
    // TODO for Bryan: Upgrade SOFT_STOP to HARD_STOP on 'no second source sought'?
    // See aip-debug/bank-open-questions.md OQ2.
    options: [
      {
        key: "confirmed_by_eb",
        label: "Confirmed by economic buyer or a second senior stakeholder independently",
        flag: "OK",
        points: 3,
      },
      {
        key: "confirmed_by_second",
        label: "Confirmed by a second stakeholder (not the EB)",
        flag: "AMBER",
        points: 1,
      },
      {
        key: "champion_only",
        label: "Only the champion has cited this date",
        flag: "AMBER",
        points: 0,
      },
      {
        key: "no_second_source",
        label: "No second source sought",
        flag: "SOFT_STOP",
        points: 0,
      },
    ],
  },
  {
    key: "cp8_slip_consequence",
    cp: 8,
    label: "What happens if this deal slips one quarter?",
    helpText:
      "Distinguishes a real deadline from an aspirational one. A buyer who cannot name a consequence of a one-quarter slip has no external forcing function.",
    options: [
      {
        key: "named_consequence",
        label: "Named consequence — specific financial, compliance, or operational impact of a slip",
        flag: "OK",
        points: 3,
      },
      {
        key: "inconvenient_no_specifics",
        label: "Buyer acknowledges a slip would be inconvenient but cannot name a specific consequence",
        flag: "AMBER",
        points: 1,
      },
      {
        key: "not_asked",
        label: "Buyer has not been asked; consequence of a slip is unknown",
        flag: "AMBER",
        points: 0,
      },
      {
        key: "no_material_impact",
        label: "Buyer states a slip would have no material impact",
        flag: "SOFT_STOP",
        points: 0,
      },
    ],
  },
];

// =====================================================================
// CP 9 — Competitive Position
// Live: 3 questions (max 10 pts)
// New: 2 questions (expansion proposal)
// =====================================================================
const CP9_QUESTIONS: QuestionDef[] = [
  // ----- LIVE (3 questions) -----
  {
    key: "cp9_landscape_known",
    cp: 9,
    label: "Competitive landscape known?",
    options: [
      { key: "all", label: "Named all competitors", flag: "OK", points: 3 },
      { key: "some", label: "Named some", flag: "AMBER", points: 1 },
      { key: "blind", label: "Blind", flag: "AMBER", points: 0 },
    ],
  },
  {
    key: "cp9_preferred_status",
    cp: 9,
    label: "Our preferred status?",
    options: [
      {
        key: "confirmed",
        label: "Confirmed by buyer — we're preferred",
        flag: "OK",
        points: 4,
      },
      {
        key: "signaled",
        label: "Signaled — not confirmed",
        flag: "AMBER",
        points: 2,
      },
      { key: "unknown", label: "Unknown", flag: "AMBER", points: 0 },
      {
        key: "not_preferred",
        label: "Not preferred (SOFT STOP)",
        flag: "SOFT_STOP",
        points: 0,
      },
    ],
  },
  {
    key: "cp9_decision_criteria",
    cp: 9,
    label: "Decision criteria mapped & weighted?",
    options: [
      {
        key: "we_wrote",
        label: "We wrote them with the buyer",
        flag: "OK",
        points: 3,
      },
      {
        key: "they_wrote",
        label: "They wrote them — we know weights",
        flag: "AMBER",
        points: 1,
      },
      { key: "unknown", label: "Unknown", flag: "AMBER", points: 0 },
    ],
  },

  // ----- NEW from expansion proposal (2 questions) -----
  {
    key: "cp9_status_quo_risk",
    cp: 9,
    label: "Is 'do nothing' a credible outcome?",
    helpText:
      "In many enterprise deals the primary competitor is inaction. The three existing CP8 questions cover named vendor competition but do not surface the status quo threat.",
    options: [
      {
        key: "no_sq_unacceptable",
        label: "No — buyer has explicitly stated the status quo is unacceptable with a named consequence",
        flag: "OK",
        points: 3,
      },
      {
        key: "low_risk",
        label: "Low risk — buyer is motivated but has not named a hard consequence of deferral",
        flag: "AMBER",
        points: 1,
      },
      {
        key: "moderate_risk",
        label: "Moderate risk — buyer has deferred similar decisions before; no strong forcing function",
        flag: "AMBER",
        points: 0,
      },
      {
        key: "high_risk",
        label: "High risk — buyer's language and behavior suggest deferral is the most likely outcome",
        flag: "SOFT_STOP",
        points: 0,
      },
    ],
  },
  {
    key: "cp9_eb_competitive_view",
    cp: 9,
    label: "Economic buyer's competitive preference known?",
    helpText:
      "The champion may know the competitive landscape; the economic buyer's opinion determines the outcome. Champion-reported preference is an assumption, not a fact.",
    options: [
      {
        key: "eb_stated_preference",
        label: "EB has directly stated or signaled a preference for us",
        flag: "OK",
        points: 4,
      },
      {
        key: "champion_reports",
        label: "Champion reports EB favors us — not confirmed directly",
        flag: "AMBER",
        points: 2,
      },
      {
        key: "not_surfaced",
        label: "EB's competitive view has not been surfaced",
        flag: "AMBER",
        points: 0,
      },
      {
        key: "eb_prefers_competitor_or_sq",
        label: "EB has indicated preference for a competitor or for maintaining the status quo",
        flag: "SOFT_STOP",
        points: 0,
      },
    ],
  },
  // ===================================================================
  // Q9.3.A–D — Paper Process sub-questions (locked: S-CP9-1, 2026-05-15)
  // Bespoke option keys (not 0..4 graded scale). resolveOptionPoints in
  // OperationalHelix falls back to options[].key lookup for scoring.
  // Top-tier points scaled to 4 (from locked 5) to fit computeCpScore's
  // denom-of-4 contract; preserves OK/AMBER/SOFT_STOP routing fidelity.
  // ===================================================================
  {
    key: "cp9_paper_procurement_contact",
    cp: 9,
    label: "Procurement contact identified?",
    helpText:
      "Who is the procurement contact by name? A named contact unblocks paper. Role-only (\"someone in procurement\") is insufficient.",
    options: [
      { key: "yes-named", label: "Yes — procurement contact identified by name", flag: "OK", points: 4 },
      { key: "yes-role-only", label: "Yes — role identified but not named contact", flag: "AMBER", points: 2 },
      { key: "no", label: "No — procurement contact not identified", flag: "SOFT_STOP", points: 0 },
    ],
    softStopMessage:
      "Procurement contact not identified. Name the contact before paper can advance. Manager override required to proceed. (RULE-ESC-1 eligible)",
  },
  {
    key: "cp9_redline_cycle_status",
    cp: 9,
    label: "Contract redline cycle status?",
    helpText:
      "Has the redline cycle been initiated? A deal cannot close without going through redlines.",
    options: [
      { key: "yes-completed", label: "Yes — redline cycle completed, contract agreed", flag: "OK", points: 4 },
      { key: "yes-in-progress", label: "Yes — redline cycle in progress", flag: "OK", points: 3 },
      { key: "no-not-started", label: "No — redline cycle not started", flag: "SOFT_STOP", points: 0 },
    ],
    softStopMessage:
      "Redline cycle has not started. Contract cannot close without a redline cycle. Manager override required to proceed. (RULE-ESC-1 eligible)",
  },
  {
    key: "cp9_security_it_review",
    cp: 9,
    label: "Security/IT review status?",
    helpText:
      "Is security/IT review complete, scheduled, pending, or not required for this engagement?",
    options: [
      { key: "complete", label: "Complete — security/IT review finished and cleared", flag: "OK", points: 4 },
      { key: "scheduled", label: "Scheduled — security/IT review has a confirmed date", flag: "OK", points: 2 },
      { key: "pending", label: "Pending — review needed but not yet scheduled", flag: "AMBER", points: 1 },
      { key: "not-required", label: "Not required — security/IT review waived for this engagement", flag: "OK", points: 3 },
    ],
  },
  {
    key: "cp9_signing_authority",
    cp: 9,
    label: "Signing authority?",
    helpText:
      "Who will sign the contract? A named signer is required. \"Role known, name pending\" is AMBER; unclear is SOFT_STOP.",
    options: [
      { key: "signed-name-known", label: "Signer identified by name (and title)", flag: "OK", points: 4 },
      { key: "role-known-name-pending", label: "Role known but signer's name not yet confirmed", flag: "AMBER", points: 2 },
      { key: "unclear", label: "Signing authority unclear", flag: "SOFT_STOP", points: 0 },
    ],
    softStopMessage:
      "Signing authority is unclear. The deal cannot close without a named signer. Manager override required to proceed. (RULE-ESC-1 eligible)",
  },
];

// =====================================================================
// CP 10 — Legal (HARD-KILL gate)
// Legal framework: paper track, surprises, mutual close plan, written commit.
// Four questions (22 pts total).
// =====================================================================
const CP10_QUESTIONS: QuestionDef[] = [
  // ----- Legal questions (4 questions) -----
  {
    key: "cp10_paper_track_mapped",
    cp: 10,
    label: "Paper track mapped — MSA, redlines, contract terms?",
    helpText:
      "What does their paper process look like — master agreement, statement of work, security review, data processing addendum, insurance, redlines?",
    options: [
      { key: "paper_open_scoped", label: "MSA, redlines, contract terms — paper path open and scoped", flag: "OK", points: 5 },
      { key: "paper_known_unconfirmed", label: "Paper process known — timeline not yet confirmed", flag: "AMBER", points: 2 },
      { key: "paper_assumed", label: "Standard procurement assumed — not yet verified", flag: "AMBER", points: 1 },
      { key: "no_paper_track", label: "No paper track mapped", flag: "RED", points: 0 },
    ],
  },
  {
    key: "cp10_surprises_sequenced",
    cp: 10,
    label: "Legal surprises sequenced now?",
    helpText:
      "Legal adds two weeks for every surprise. Sequence the surprises now or watch the deal slip in week eleven.",
    options: [
      { key: "all_sequenced", label: "All known surprises sequenced — no hidden steps", flag: "OK", points: 5 },
      { key: "partial_sequenced", label: "Some potential surprises identified — not fully sequenced", flag: "AMBER", points: 2 },
      { key: "not_sequenced", label: "No sequencing done", flag: "RED", points: 0 },
    ],
  },
  {
    key: "cp10_close_plan_signed",
    cp: 10,
    label: "Mutual close plan signed by champion?",
    helpText:
      "If the champion will not sign the close plan, ask why. Their answer is the deal.",
    options: [
      { key: "plan_signed", label: "Mutual close plan signed by champion and AE", flag: "OK", points: 5 },
      { key: "plan_drafted", label: "Closing plan drafted — not yet signed by champion", flag: "AMBER", points: 2 },
      { key: "plan_ae_only", label: "AE has a plan — champion does not know it exists", flag: "AMBER", points: 1 },
      { key: "no_plan", label: "No close plan", flag: "RED", points: 0 },
    ],
  },
  // ----- Written Commitment (LIVE 1 question) -----
  {
    key: "cp10_written_commit",
    cp: 10,
    label: "Written commitment received?",
    options: [
      { key: "signed", label: "Signed", flag: "OK", points: 5 },
      { key: "verbal", label: "Verbal only", flag: "AMBER", points: 2 },
      { key: "none", label: "No commit (HARD STOP)", flag: "HARD_STOP", points: 0 },
    ],
  },

  // ----- NEW from expansion proposal (1 question) -----
  {
    key: "cp10_commitment_type",
    cp: 10,
    label: "What form is the written commitment?",
    helpText:
      "All written commitments are not equal for forecast purposes. A countersigned order form differs from a signed LOI, which differs from a signed MSA.",
    // TODO for Bryan: Should a signed LOI alone clear the CP9 hard-kill gate?
    // See aip-debug/bank-open-questions.md OQ5.
    // Points scaled: max reduced from 5 → 2 per Decision 2 (bank-decisions-LOGGED.md).
    options: [
      {
        key: "signed_order_po",
        label: "Signed order form or PO — revenue recognized",
        flag: "OK",
        points: 2,
      },
      {
        key: "signed_msa_sow_pending",
        label: "Signed MSA with SOW pending — execution in progress",
        flag: "OK",
        points: 2,
      },
      {
        key: "signed_loi",
        label: "Signed LOI — commercial intent confirmed, contract not yet executed",
        flag: "AMBER",
        points: 1,
      },
      {
        key: "signed_mutual_close_plan",
        label: "Signed mutual close plan only — intent to close, not yet commercial commitment",
        flag: "AMBER",
        points: 0,
      },
    ],
  },

  {
    key: "cp10_technical_owner_cutover",
    cp: 10,
    label: "Has the customer's technical team confirmed they can absorb the integration without rebuilding their existing stack — and is there a named technical owner accountable for the cutover?",
    helpText:
      "No named technical owner means implementation risk defaults to your team. A customer who cannot name the person accountable for the cutover is not ready to close.",
    options: [
      { key: "confirmed_named_owner", label: "Technical stack compatibility confirmed and cutover owner named in writing", flag: "OK", points: 5 },
      { key: "verbal_no_owner",       label: "Stack compatibility verbal — no named technical owner yet",                 flag: "AMBER", points: 2 },
      { key: "not_assessed",          label: "Integration complexity not yet assessed by customer",                        flag: "AMBER", points: 1 },
      { key: "rebuild_required",      label: "Rebuild required — deal is blocked at technical layer",                     flag: "HARD_STOP", points: 0 },
    ],
  },
  {
    key: "cp10_deployment_risks_owned",
    cp: 10,
    label: "Are the highest-impact deployment risks — change management, data migration, downtime windows, training capacity — identified, scoped, and owned by the customer's side?",
    helpText:
      "Deployment risk that lands on your team after the close is a margin and satisfaction problem. The customer must own the risk inventory before you sign.",
    options: [
      { key: "all_risks_owned",      label: "All major deployment risks named, scoped, and customer-owned with a named lead", flag: "OK",    points: 5 },
      { key: "partial_risks_owned",  label: "Key risks identified — ownership partial or informal",                          flag: "AMBER", points: 2 },
      { key: "risks_unidentified",   label: "Risk inventory not yet completed",                                              flag: "AMBER", points: 1 },
      { key: "risks_unowned",        label: "Customer expects vendor to own deployment risk",                                flag: "SOFT_STOP", points: 0 },
    ],
  },
  {
    key: "cp10_change_management_owner",
    cp: 10,
    label: "Is there a named change-management owner on the customer side with the mandate and authority to drive adoption past the go-live finish line?",
    helpText:
      "Go-live without adoption is a churn event. The customer needs a named human accountable for behavior change — not just a project tracker.",
    options: [
      { key: "named_cm_owner",        label: "Named change-management owner confirmed, mandate in writing",   flag: "OK",    points: 4 },
      { key: "informally_assigned",   label: "Informally assigned — no formal mandate",                      flag: "AMBER", points: 2 },
      { key: "no_owner_planned",      label: "No change-management owner planned",                           flag: "SOFT_STOP", points: 0 },
    ],
  },
  {
    key: "cp10_prior_implementation_record",
    cp: 10,
    label: "Has the customer successfully deployed a system of similar complexity before — and if they have failed previously, is the root cause understood and addressed?",
    helpText:
      "Prior failed implementations are the single strongest predictor of post-close churn. An organization that has failed before and cannot name what changed is a higher-risk close.",
    options: [
      { key: "successful_prior",    label: "Successful comparable deployment on record",                          flag: "OK",    points: 3 },
      { key: "no_prior_ready",      label: "No prior deployment — change capacity assessed as adequate",          flag: "OK",    points: 2 },
      { key: "prior_failure_fixed", label: "Prior failure — root cause named and corrective action confirmed",    flag: "AMBER", points: 1 },
      { key: "prior_failure_open",  label: "Prior failure — root cause not named or not corrected",              flag: "SOFT_STOP", points: 0 },
    ],
  },

  // ----- Risk + Implementation EXPANSION (10 questions — added BUILD-15) -----

  // Implementation timeline alignment
  {
    key: "cp10_go_live_date_confirmed",
    cp: 10,
    label: "Is there a mutually agreed go-live date with a project plan supporting it?",
    helpText:
      "A verbal target is not a plan. A real go-live date is backed by a customer-side project plan, named milestones, and resource commitments. Without it, 'we want to be live by Q3' is decoration.",
    options: [
      {
        key: "date_plan_resourced",
        label: "Go-live date agreed in writing — project plan with milestones and named resources in place",
        flag: "OK",
        points: 4,
      },
      {
        key: "date_agreed_no_plan",
        label: "Go-live date agreed verbally — no supporting project plan yet",
        flag: "AMBER",
        points: 2,
      },
      {
        key: "target_only",
        label: "Target quarter stated — no firm date agreed with customer",
        flag: "AMBER",
        points: 1,
      },
      {
        key: "no_date",
        label: "No go-live date — implementation timeline is open-ended",
        flag: "SOFT_STOP",
        points: 0,
      },
    ],
  },

  // Champion + executive sponsor durability
  {
    key: "cp10_champion_reorg_risk",
    cp: 10,
    label: "Is the champion or executive sponsor at risk of losing their role, scope, or mandate before go-live?",
    helpText:
      "A champion who is leaving, being restructured out, or whose mandate is narrowing is a deal risk that no technical plan resolves. If the champion who sold this internally is gone at go-live, the project is an orphan.",
    options: [
      {
        key: "stable_confirmed",
        label: "Both champion and exec sponsor confirmed stable — no known org change pending",
        flag: "OK",
        points: 3,
      },
      {
        key: "stable_unverified",
        label: "Appears stable — rep has not explicitly verified with the champion",
        flag: "AMBER",
        points: 1,
      },
      {
        key: "reorg_in_flight",
        label: "Org restructure underway — champion's or exec sponsor's role is changing",
        flag: "SOFT_STOP",
        points: 0,
      },
      {
        key: "champion_leaving",
        label: "Champion or exec sponsor is departing before go-live",
        flag: "SOFT_STOP",
        points: 0,
      },
    ],
  },

  // Success metrics defined and measurable
  {
    key: "cp10_success_metrics_defined",
    cp: 10,
    label: "Have the customer's success metrics for this implementation been defined, agreed, and attached to a measurement timeline?",
    helpText:
      "Without agreed success metrics, every post-go-live review is a political conversation. Defined metrics protect both the customer's investment case and your renewal path.",
    options: [
      {
        key: "metrics_defined_signed",
        label: "Success metrics defined, agreed with the customer, and documented — measurement timeline confirmed",
        flag: "OK",
        points: 4,
      },
      {
        key: "metrics_defined_unsigned",
        label: "Metrics discussed and drafted — not yet formally agreed with customer",
        flag: "AMBER",
        points: 2,
      },
      {
        key: "metrics_vague",
        label: "Vague outcomes named ('better visibility', 'more accountability') — no measurable targets",
        flag: "AMBER",
        points: 1,
      },
      {
        key: "no_metrics",
        label: "No success metrics defined",
        flag: "SOFT_STOP",
        points: 0,
      },
    ],
  },

  // Rollback / contingency planning
  {
    key: "cp10_rollback_plan",
    cp: 10,
    label: "Is there a rollback or contingency plan if the go-live fails or the system must be pulled back?",
    helpText:
      "Rollback planning is not pessimism — it is the signal that the customer has thought through the operational risk seriously. A customer with no rollback plan has not assessed the impact of failure.",
    options: [
      {
        key: "rollback_documented",
        label: "Rollback plan documented and agreed — owner named, triggers defined",
        flag: "OK",
        points: 3,
      },
      {
        key: "rollback_discussed",
        label: "Rollback discussed — not yet documented or formally agreed",
        flag: "AMBER",
        points: 1,
      },
      {
        key: "no_rollback",
        label: "No rollback plan — customer has not addressed failure scenario",
        flag: "AMBER",
        points: 0,
      },
    ],
  },

  // Adoption strategy — training, enablement, accountability
  {
    key: "cp10_training_plan_funded",
    cp: 10,
    label: "Is there a funded, scheduled training and enablement plan covering all user groups who will operate the system at go-live?",
    helpText:
      "Training that is not funded, not scheduled, or not covering frontline users produces a system with no adoption. The plan must cover every group that will use the product — not just power users.",
    options: [
      {
        key: "funded_scheduled_all_cohorts",
        label: "Funded training plan with a schedule covering all user groups — owner named",
        flag: "OK",
        points: 4,
      },
      {
        key: "funded_power_users_only",
        label: "Funded plan covering power users — frontline or field users not yet included",
        flag: "AMBER",
        points: 2,
      },
      {
        key: "plan_no_budget",
        label: "Training plan drafted — no budget allocated, no schedule set",
        flag: "AMBER",
        points: 1,
      },
      {
        key: "no_training_plan",
        label: "No training plan — assumed to be handled informally",
        flag: "SOFT_STOP",
        points: 0,
      },
    ],
  },

  // ROI timeline expectations
  {
    key: "cp10_roi_timeline_realistic",
    cp: 10,
    label: "Has the customer acknowledged a realistic time-to-value window and aligned their expectations to it?",
    helpText:
      "A customer expecting full ROI in 30 days from a multi-site deployment will declare the implementation a failure at 60 days. Misaligned ROI timeline expectations are the most common source of post-close dissatisfaction.",
    options: [
      {
        key: "timeline_agreed_documented",
        label: "Realistic ROI timeline agreed, documented, and signed into the success plan",
        flag: "OK",
        points: 3,
      },
      {
        key: "timeline_discussed_verbal",
        label: "ROI timeline discussed — customer acknowledged verbally but not in writing",
        flag: "AMBER",
        points: 1,
      },
      {
        key: "unrealistic_expectations",
        label: "Customer has stated expectations that are materially inconsistent with a realistic implementation timeline",
        flag: "SOFT_STOP",
        points: 0,
      },
      {
        key: "not_discussed",
        label: "ROI timeline not discussed — customer's expectations are unknown",
        flag: "AMBER",
        points: 0,
      },
    ],
  },

  // Procurement / contracting risk
  {
    key: "cp10_procurement_timeline_modeled",
    cp: 10,
    label: "Has the full procurement and contracting timeline — including legal review, IT security, insurance, and signature authority — been modeled into the go-live date?",
    helpText:
      "Procurement cycles that are not modeled into the project plan push go-live dates without warning. Every legal or IT review that runs in parallel with implementation is a risk. The customer's procurement cycle must be inside the plan, not assumed away.",
    options: [
      {
        key: "fully_modeled",
        label: "Full procurement and contracting timeline modeled — no steps assumed away",
        flag: "OK",
        points: 4,
      },
      {
        key: "partially_modeled",
        label: "Commercial steps modeled — IT security or insurance reviews not yet scoped into the plan",
        flag: "AMBER",
        points: 2,
      },
      {
        key: "not_modeled",
        label: "Procurement timeline not modeled into the go-live plan",
        flag: "AMBER",
        points: 0,
      },
      {
        key: "procurement_blocker",
        label: "Known procurement or contracting blocker that cannot be resolved within the agreed timeline",
        flag: "SOFT_STOP",
        points: 0,
      },
    ],
  },

  // Stakeholder alignment beyond signer
  {
    key: "cp10_implementation_stakeholder_alignment",
    cp: 10,
    label: "Are the key stakeholders who will be affected by the rollout — beyond the economic buyer and champion — aligned on scope, timing, and their own responsibilities?",
    helpText:
      "The signer is rarely the person who determines post-go-live success. Department heads, plant managers, IT leads, and frontline supervisors whose workflows change at go-live must be aligned before close — not surprised after.",
    options: [
      {
        key: "all_aligned",
        label: "All affected stakeholders briefed, aligned on scope and timeline, and have accepted their responsibilities",
        flag: "OK",
        points: 4,
      },
      {
        key: "primary_aligned_others_not_reached",
        label: "Primary user groups aligned — downstream or operational stakeholders not yet reached",
        flag: "AMBER",
        points: 2,
      },
      {
        key: "champion_only_aligned",
        label: "Only champion and economic buyer aligned — team-level stakeholders not engaged",
        flag: "AMBER",
        points: 1,
      },
      {
        key: "unaligned_known_resistance",
        label: "Known resistance from operational stakeholders who will be required to use the system",
        flag: "SOFT_STOP",
        points: 0,
      },
    ],
  },

  // Data migration readiness
  {
    key: "cp10_data_migration_ready",
    cp: 10,
    label: "Is the customer's data migration scope defined, owned, and resourced — including data quality remediation if required?",
    helpText:
      "Data migrations that arrive late, dirty, or unowned at go-live are the most common technical cause of implementation overruns. The customer must own the data work, not the vendor.",
    options: [
      {
        key: "scoped_owned_clean",
        label: "Migration scope defined, customer-owned, data quality confirmed adequate — timeline in plan",
        flag: "OK",
        points: 4,
      },
      {
        key: "scoped_owned_cleanup_needed",
        label: "Migration scope defined and owned — data quality issues identified with a remediation plan",
        flag: "AMBER",
        points: 2,
      },
      {
        key: "scoped_not_owned",
        label: "Migration scope identified — no named customer owner or timeline yet",
        flag: "AMBER",
        points: 1,
      },
      {
        key: "migration_not_scoped",
        label: "Data migration scope not yet assessed",
        flag: "AMBER",
        points: 0,
      },
      {
        key: "migration_blocker",
        label: "Data migration blocker identified — scope, quality, or ownership problem cannot be resolved within the implementation window",
        flag: "SOFT_STOP",
        points: 0,
      },
    ],
  },

  // Internal change management capacity
  {
    key: "cp10_change_capacity_confirmed",
    cp: 10,
    label: "Does the customer's internal team have the bandwidth to absorb this implementation alongside their current operational workload?",
    helpText:
      "An implementation with no spare capacity on the customer side runs at vendor speed, not customer speed. A team already at capacity on other projects will defer, delay, and ultimately blame the vendor. Capacity must be confirmed — not assumed.",
    options: [
      {
        key: "capacity_confirmed_dedicated",
        label: "Dedicated implementation resource confirmed on the customer side — ring-fenced capacity",
        flag: "OK",
        points: 4,
      },
      {
        key: "capacity_shared_assessed",
        label: "Shared resource — customer has assessed workload and confirmed bandwidth is available",
        flag: "AMBER",
        points: 2,
      },
      {
        key: "capacity_assumed",
        label: "Capacity assumed — no explicit workload assessment done by the customer",
        flag: "AMBER",
        points: 1,
      },
      {
        key: "capacity_overloaded",
        label: "Customer team is overloaded with competing priorities — implementation capacity cannot be confirmed",
        flag: "SOFT_STOP",
        points: 0,
      },
    ],
  },
];

// =====================================================================
// INDUSTRY SUB-QUESTIONS — Automotive (existing 4 keys, unchanged)
// =====================================================================
const AUTOMOTIVE_QUESTIONS: QuestionDef[] = [
  {
    key: "auto_tier",
    cp: 1,
    industryTrigger: "Automotive",
    label: "Automotive — Tier 1 or Tier 2 supplier?",
    options: [
      { key: "tier1", label: "Tier 1", flag: "OK", points: 2 },
      { key: "tier2", label: "Tier 2", flag: "OK", points: 1 },
      { key: "oem", label: "OEM", flag: "OK", points: 2 },
    ],
  },
  {
    key: "auto_ev_pressure",
    cp: 1,
    industryTrigger: "Automotive",
    label: "Automotive — Active EV transition cost pressure?",
    options: [
      { key: "yes_confirmed", label: "Yes — confirmed", flag: "OK", points: 2 },
      { key: "suspected", label: "Suspected", flag: "AMBER", points: 1 },
      { key: "no", label: "No", flag: "AMBER", points: 0 },
    ],
  },
  {
    key: "auto_oem_mandate",
    cp: 1,
    industryTrigger: "Automotive",
    label: "Automotive — OEM cost-down mandate cascading down?",
    options: [
      { key: "yes_confirmed", label: "Yes — confirmed", flag: "OK", points: 3 },
      { key: "suspected", label: "Suspected", flag: "AMBER", points: 1 },
      { key: "no", label: "No", flag: "AMBER", points: 0 },
    ],
  },
  {
    key: "auto_bryan_network",
    cp: 2,
    industryTrigger: "Automotive",
    label: "Automotive — Account in Bryan's Karamba / BG Networks coverage?",
    options: [
      { key: "yes", label: "Yes — warm path", flag: "OK", points: 1 },
      { key: "unknown", label: "Unknown", flag: "AMBER", points: 0 },
    ],
  },
];

// HEALTHCARE_QUESTIONS removed 2026-05-07: hospital/clinic provider context
// is off-domain for the manufacturing-only positioning.
// PRIVATE_EQUITY_QUESTIONS removed 2026-05-07: PE-firm / portfolio context
// is off-domain for the manufacturing-only positioning.

// =====================================================================
// INDUSTRY SUB-QUESTIONS — Aerospace (24 new keys, replaces 2 stubs)
// =====================================================================
const AEROSPACE_QUESTIONS: QuestionDef[] = [
  // Spine — fires for all Aerospace accounts regardless of sub-vertical
  {
    key: "aero_spine_as9100",
    cp: 3,
    industryTrigger: "Aerospace",
    label: "Aerospace (All) — AS9100 / AS9110 certification status?",
    helpText:
      "AS9100 (manufacturing/MRO) or AS9110 (MRO-only) cert is the baseline quality language in this industry. Not certified where customers require it is a structural constraint, not a process gap.",
    options: [
      {
        key: "certified_current",
        label: "AS9100/AS9110 certified, current audit cycle",
        flag: "OK",
        points: 3,
      },
      {
        key: "certified_gap",
        label: "Certified but audit gap or CAR open",
        flag: "AMBER",
        points: 1,
      },
      {
        key: "not_certified_not_required",
        label: "Not certified — customer base does not require it",
        flag: "AMBER",
        points: 1,
      },
      {
        key: "not_certified_required",
        label: "Not certified — customer requires it, working toward it",
        flag: "HARD_STOP",
        points: 0,
      },
    ],
  },
  {
    key: "aero_spine_flowdown",
    cp: 3,
    industryTrigger: "Aerospace",
    label: "Aerospace (All) — Certification flow-down obligations from prime?",
    helpText:
      "Primes flow down quality, security, and traceability requirements to all Tier suppliers. Understanding what flows down shapes where our solution can and cannot touch.",
    options: [
      {
        key: "full_flowdown_mapped",
        label: "Full flow-down requirements documented and mapped internally",
        flag: "OK",
        points: 2,
      },
      {
        key: "flowdown_partial",
        label: "Partially mapped — gaps exist",
        flag: "AMBER",
        points: 1,
      },
      {
        key: "flowdown_unknown",
        label: "Not sure what flows down from prime",
        flag: "AMBER",
        points: 0,
      },
      {
        key: "no_prime_relationship",
        label: "No prime relationship — commercial customers only",
        flag: "OK",
        points: 1,
      },
    ],
  },
  {
    key: "aero_spine_aftermarket_ratio",
    cp: 7,
    industryTrigger: "Aerospace",
    label: "Aerospace (All) — Aftermarket / MRO revenue vs. OE production ratio?",
    helpText:
      "Aftermarket margins are typically higher and more recurring than OE production. A company with significant MRO revenue has a different economic profile and different urgency driver.",
    options: [
      {
        key: "majority_aftermarket",
        label: "Majority aftermarket / MRO (>50% revenue)",
        flag: "OK",
        points: 3,
      },
      {
        key: "balanced",
        label: "Roughly balanced OE / aftermarket",
        flag: "OK",
        points: 2,
      },
      {
        key: "majority_oe",
        label: "Majority OE production (>70% revenue)",
        flag: "OK",
        points: 1,
      },
      { key: "unknown", label: "Unknown mix", flag: "AMBER", points: 0 },
    ],
  },

  // Commercial Aerospace sub-vertical (7 questions)
  {
    key: "aero_commercial_buildrate",
    cp: 3,
    industryTrigger: "Aerospace",
    subVertical: "commercial",
    label: "Aerospace (Commercial) — Build-rate cycle phase for primary programs?",
    helpText:
      "Build-rate phase is the single most important economic context signal in commercial aero. Rate ramp forces capacity investment; rate reduction forces cost reduction.",
    options: [
      {
        key: "rate_ramp",
        label: "Active rate ramp on primary program (e.g., 737, A320, 787)",
        flag: "OK",
        points: 4,
      },
      {
        key: "steady_state",
        label: "Steady state — no near-term rate change anticipated",
        flag: "OK",
        points: 2,
      },
      {
        key: "rate_reduction",
        label: "Rate reduction or program pause in effect",
        flag: "OK",
        points: 3,
      },
      {
        key: "multi_program_mixed",
        label: "Multi-program — mixed signals (some up, some down)",
        flag: "AMBER",
        points: 1,
      },
      { key: "unknown", label: "Unknown / not discussed", flag: "AMBER", points: 0 },
    ],
  },
  {
    key: "aero_commercial_tier",
    cp: 1,
    industryTrigger: "Aerospace",
    subVertical: "commercial",
    label: "Aerospace (Commercial) — Tier position on primary program?",
    helpText:
      "Tier 1 suppliers have direct Boeing/Airbus contracts and direct pressure. Tier 2/3 suppliers experience pressure indirectly.",
    options: [
      {
        key: "tier1_direct",
        label: "Tier 1 — direct OEM contract (Boeing/Airbus/Embraer)",
        flag: "OK",
        points: 3,
      },
      {
        key: "tier2_prime",
        label: "Tier 2 — contract with Spirit, Collins, Honeywell, Safran, GKN, etc.",
        flag: "OK",
        points: 2,
      },
      {
        key: "tier3_sub",
        label: "Tier 3 — subcontract to a Tier 2",
        flag: "AMBER",
        points: 1,
      },
      { key: "unknown", label: "Tier position unclear", flag: "AMBER", points: 0 },
    ],
  },
  {
    key: "aero_commercial_buying_committee",
    cp: 4,
    industryTrigger: "Aerospace",
    subVertical: "commercial",
    label: "Aerospace (Commercial) — Organizational Mapping: who controls program operational spend?",
    helpText:
      "In commercial aero, the Program Manager owns the schedule and P&L for the program. A rep without Program Manager access is selling into a void.",
    options: [
      {
        key: "pm_and_ops",
        label: "Program Manager and Operations Director both engaged",
        flag: "OK",
        points: 4,
      },
      {
        key: "pm_only",
        label: "Program Manager engaged, Ops not yet",
        flag: "OK",
        points: 2,
      },
      {
        key: "ops_only",
        label: "Operations or plant level only — no Program Manager",
        flag: "AMBER",
        points: 1,
      },
      {
        key: "procurement_only",
        label: "Only procurement / sourcing engaged",
        flag: "SOFT_STOP",
        points: 0,
      },
    ],
  },
  {
    key: "aero_commercial_cert_change",
    cp: 10,
    industryTrigger: "Aerospace",
    subVertical: "commercial",
    label: "Aerospace (Commercial) — FAA/EASA certification interaction with operational change?",
    helpText:
      "Any change to a certified process, tooling, or supplier on an FAA/EASA-approved part triggers a DER review. Ask explicitly: does our solution touch any certified process or approved supplier list?",
    options: [
      {
        key: "no_cert_touch",
        label: "Solution does not touch certified processes or approved supplier list",
        flag: "OK",
        points: 3,
      },
      {
        key: "cert_touch_managed",
        label: "Touches certified processes — change management process understood and resourced",
        flag: "OK",
        points: 2,
      },
      {
        key: "cert_touch_unknown_path",
        label: "Touches certified processes — change management path unclear",
        flag: "AMBER",
        points: 1,
      },
      {
        key: "cert_required_no_path",
        label: "Cert change required, no DER or approval path identified",
        flag: "HARD_STOP",
        points: 0,
      },
    ],
  },
  {
    key: "aero_commercial_supply_pressure",
    cp: 3,
    industryTrigger: "Aerospace",
    subVertical: "commercial",
    label: "Aerospace (Commercial) — Supply chain disruption pressure (single-source, long-lead castings/forgings)?",
    helpText:
      "Titanium forgings, aluminum castings, and composite raw materials are on multi-year lead times. This reveals whether the cost problem is internal (solvable) or external (structural).",
    options: [
      {
        key: "secure_supply",
        label: "Supply secured for current rate — LTAs in place",
        flag: "OK",
        points: 3,
      },
      {
        key: "partial_gap",
        label: "Partial gaps — some single-source exposure being worked",
        flag: "AMBER",
        points: 1,
      },
      {
        key: "significant_gap",
        label: "Significant long-lead exposure — at-risk for rate ramp",
        flag: "AMBER",
        points: 1,
      },
      { key: "unknown", label: "Not discussed / unknown", flag: "AMBER", points: 0 },
    ],
  },
  {
    key: "aero_commercial_oe_vs_afm",
    cp: 7,
    industryTrigger: "Aerospace",
    subVertical: "commercial",
    label: "Aerospace (Commercial) — OE vs. aftermarket economic split and margin visibility?",
    helpText:
      "Meaningful aftermarket/MRO revenue means higher-margin recurring work — a very different financial profile than pure OE.",
    options: [
      {
        key: "afm_gt_40pct",
        label: "Aftermarket >40% of revenue — strong recurring margin base",
        flag: "OK",
        points: 3,
      },
      {
        key: "afm_10_40pct",
        label: "Aftermarket 10–40% — growing MRO book",
        flag: "OK",
        points: 2,
      },
      {
        key: "pure_oe",
        label: "Pure OE production (<10% aftermarket)",
        flag: "OK",
        points: 1,
      },
      { key: "unknown", label: "Mix unknown", flag: "AMBER", points: 0 },
    ],
  },
  {
    key: "aero_commercial_nadcap",
    cp: 3,
    industryTrigger: "Aerospace",
    subVertical: "commercial",
    label: "Aerospace (Commercial) — NADCAP accreditation status for special processes?",
    helpText:
      "NADCAP covers heat treat, NDT, chemical processing, welding. An audit cycle due within 6 months is a real urgency driver.",
    options: [
      {
        key: "nadcap_current_no_cars",
        label: "NADCAP accredited, current, no open CARs",
        flag: "OK",
        points: 2,
      },
      {
        key: "nadcap_current_open_cars",
        label: "Accredited but CARs open — audit pressure",
        flag: "AMBER",
        points: 1,
      },
      {
        key: "nadcap_renewal_due",
        label: "Renewal audit due within 6 months",
        flag: "OK",
        points: 3,
      },
      {
        key: "nadcap_na",
        label: "NADCAP not applicable (no special processes)",
        flag: "OK",
        points: 1,
      },
      {
        key: "nadcap_lapsed",
        label: "Accreditation lapsed or at risk",
        flag: "HARD_STOP",
        points: 0,
      },
    ],
  },

  // Defense sub-vertical (7 questions)
  {
    key: "aero_defense_itar",
    cp: 3,
    industryTrigger: "Aerospace",
    subVertical: "defense",
    label: "Aerospace (Defense) — ITAR / EAR registration and compliance posture?",
    helpText:
      "ITAR registration is a legal prerequisite to manufacturing, exporting, or discussing certain defense-related technical data. No registration = no work on covered programs.",
    options: [
      {
        key: "itar_registered_active",
        label: "ITAR registered, active, compliance program in place",
        flag: "OK",
        points: 3,
      },
      {
        key: "itar_registered_gaps",
        label: "Registered but compliance program has gaps or pending consent agreement",
        flag: "AMBER",
        points: 1,
      },
      {
        key: "ear_only",
        label: "EAR-controlled only (no ITAR) — dual-use items",
        flag: "OK",
        points: 2,
      },
      {
        key: "not_registered_not_required",
        label: "Not registered — product/service does not require it",
        flag: "OK",
        points: 1,
      },
      {
        key: "not_registered_required",
        label: "Not registered — customer or program requires it",
        flag: "HARD_STOP",
        points: 0,
      },
    ],
  },
  {
    key: "aero_defense_contract_type",
    cp: 7,
    industryTrigger: "Aerospace",
    subVertical: "defense",
    label: "Aerospace (Defense) — Contract type on primary program(s)?",
    helpText:
      "Cost-plus means cost reductions reduce fee; FFP means every dollar saved is margin. The value story and the economic buyer's incentive are completely different.",
    options: [
      {
        key: "ffp_primary",
        label: "Primarily firm-fixed-price (FFP) — every dollar saved is margin",
        flag: "OK",
        points: 4,
      },
      {
        key: "mixed_ffp_cp",
        label: "Mix of FFP and cost-plus programs",
        flag: "OK",
        points: 2,
      },
      {
        key: "cost_plus_primary",
        label: "Primarily cost-plus (CPFF/CPAF) — incentive structure different",
        flag: "AMBER",
        points: 1,
      },
      { key: "unknown", label: "Contract type not known", flag: "AMBER", points: 0 },
    ],
  },
  {
    key: "aero_defense_program_por",
    cp: 3,
    industryTrigger: "Aerospace",
    subVertical: "defense",
    label: "Aerospace (Defense) — Program-of-record status and funding horizon?",
    helpText:
      "A program-of-record with multi-year procurement authority is a durable revenue stream. LRIP to FRP transition is a real urgency event.",
    options: [
      {
        key: "fpr_full_rate",
        label: "Full Rate Production — stable, multi-year funded",
        flag: "OK",
        points: 3,
      },
      {
        key: "lrip_to_frp",
        label: "LRIP transitioning to FRP — rate ramp urgency",
        flag: "OK",
        points: 4,
      },
      {
        key: "emd_phase",
        label: "EMD / development phase — pre-production",
        flag: "AMBER",
        points: 1,
      },
      {
        key: "sustainment_only",
        label: "Sustainment / depot only — legacy platform",
        flag: "OK",
        points: 2,
      },
      { key: "unknown", label: "Program phase unknown", flag: "AMBER", points: 0 },
    ],
  },
  {
    key: "aero_defense_buying_committee",
    cp: 4,
    industryTrigger: "Aerospace",
    subVertical: "defense",
    label: "Aerospace (Defense) — Organizational Mapping: who controls cost and operational decisions?",
    helpText:
      "Defense primes have a PMO that owns schedule and cost. Access to the PMO Director or VP Programs is the critical path.",
    options: [
      {
        key: "pmo_and_contracts",
        label: "PMO Director or VP Programs + Contracts both engaged",
        flag: "OK",
        points: 4,
      },
      {
        key: "pmo_only",
        label: "PMO engaged, Contracts not yet",
        flag: "OK",
        points: 2,
      },
      {
        key: "engineering_and_pmo",
        label: "Engineering and PMO — strong technical champion",
        flag: "OK",
        points: 3,
      },
      {
        key: "contracts_only",
        label: "Contracts / procurement only — no PMO access",
        flag: "SOFT_STOP",
        points: 0,
      },
      {
        key: "no_access",
        label: "No meaningful stakeholder access yet",
        flag: "AMBER",
        points: 0,
      },
    ],
  },
  {
    key: "aero_defense_dcaa",
    cp: 3,
    industryTrigger: "Aerospace",
    subVertical: "defense",
    label: "Aerospace (Defense) — DCAA / DCMA audit exposure and cost accounting compliance?",
    helpText:
      "On cost-reimbursable contracts, DCAA audits cost accounting practices. A company under active DCAA audit finding cannot receive new cost-plus awards.",
    options: [
      {
        key: "dcaa_clean_compliant",
        label: "Cost accounting compliant, clean DCAA audit history",
        flag: "OK",
        points: 2,
      },
      {
        key: "dcaa_findings_open",
        label: "Open DCAA findings or corrective action in progress",
        flag: "AMBER",
        points: 1,
      },
      {
        key: "ffp_only_dcma",
        label: "FFP only — DCMA quality surveillance, no DCAA exposure",
        flag: "OK",
        points: 2,
      },
      { key: "unknown", label: "Audit posture unknown", flag: "AMBER", points: 0 },
    ],
  },
  {
    key: "aero_defense_sole_source",
    cp: 10,
    industryTrigger: "Aerospace",
    subVertical: "defense",
    label: "Aerospace (Defense) — Competitive switching cost: sole-source vs. competed position?",
    helpText:
      "Sole-source positions on defense programs are extremely durable. Know whether this account holds sole-source positions and whether they are at risk of re-competition.",
    options: [
      {
        key: "sole_source_primary",
        label: "Primarily sole-source positions — high switching cost incumbency",
        flag: "OK",
        points: 4,
      },
      {
        key: "mixed_sole_competed",
        label: "Mix of sole-source and competed positions",
        flag: "OK",
        points: 2,
      },
      {
        key: "primarily_competed",
        label: "Primarily competed — re-bid risk is real",
        flag: "AMBER",
        points: 1,
      },
      { key: "unknown", label: "Not known", flag: "AMBER", points: 0 },
    ],
  },
  {
    key: "aero_defense_budget_risk",
    cp: 7,
    industryTrigger: "Aerospace",
    subVertical: "defense",
    label: "Aerospace (Defense) — DoD budget cycle sensitivity (CR, sequestration, or program cut risk)?",
    helpText:
      "The U.S. defense budget running on a Continuing Resolution freezes program spending at prior-year levels and blocks new starts.",
    options: [
      {
        key: "multi_year_funded_stable",
        label: "Multi-year procurement funded, no restructure signal",
        flag: "OK",
        points: 3,
      },
      {
        key: "annual_appropriation_risk",
        label: "Annual appropriation — CR exposure, but historically funded",
        flag: "AMBER",
        points: 1,
      },
      {
        key: "program_restructure_risk",
        label: "Active program restructure or cut discussion",
        flag: "AMBER",
        points: 0,
      },
      { key: "unknown", label: "Budget posture unknown", flag: "AMBER", points: 0 },
    ],
  },

  // Space / New Space sub-vertical (7 questions)
  {
    key: "aero_space_revenue_model",
    cp: 3,
    industryTrigger: "Aerospace",
    subVertical: "new_space",
    label: "Aerospace (Space) — Primary revenue model: commercial launch vs. DoD/civil program?",
    helpText:
      "Pure commercial launch revenue is lumpy and capital-dependent. DoD/civil program revenue is more durable but comes with oversight, ITAR, and cost accounting obligations.",
    options: [
      {
        key: "dod_civil_primary",
        label: "Primarily DoD / civil programs (Space Force, SDA, NASA) — program economics",
        flag: "OK",
        points: 4,
      },
      {
        key: "mixed_commercial_gov",
        label: "Mix of commercial and government — transitioning toward DoD",
        flag: "OK",
        points: 3,
      },
      {
        key: "pure_commercial",
        label: "Pure commercial launch / satellite — capital and launch cadence driven",
        flag: "OK",
        points: 2,
      },
      {
        key: "pre_revenue",
        label: "Pre-revenue / development stage — capital burn only",
        flag: "AMBER",
        points: 0,
      },
    ],
  },
  {
    key: "aero_space_cadence",
    cp: 3,
    industryTrigger: "Aerospace",
    subVertical: "new_space",
    label: "Aerospace (Space) — Launch cadence and manufacturing rate pressure?",
    helpText:
      "New space companies competing on launch price must drive toward high-cadence, repeatable manufacturing. Ask how many vehicles per year they are targeting and whether ops is rate-ready.",
    options: [
      {
        key: "high_cadence_constrained",
        label: "Targeting >6 vehicles/year — capacity constrained, throughput is the problem",
        flag: "OK",
        points: 4,
      },
      {
        key: "moderate_cadence_scaling",
        label: "2–6 vehicles/year — actively scaling production",
        flag: "OK",
        points: 3,
      },
      {
        key: "low_cadence_prototype",
        label: "Under 2 per year — still prototype / pathfinder cadence",
        flag: "AMBER",
        points: 1,
      },
      { key: "unknown", label: "Cadence not discussed", flag: "AMBER", points: 0 },
    ],
  },
  {
    key: "aero_space_buying_committee",
    cp: 4,
    industryTrigger: "Aerospace",
    subVertical: "new_space",
    label: "Aerospace (Space) — Organizational Mapping: who controls operational and manufacturing investment decisions?",
    helpText:
      "New space companies tend to have flat org structures. The COO or VP Operations often controls both manufacturing investment and vendor selection. Rep should target operations leadership directly.",
    options: [
      {
        key: "coo_vp_ops_engaged",
        label: "COO or VP Operations engaged — decision authority clear",
        flag: "OK",
        points: 4,
      },
      {
        key: "director_ops_only",
        label: "Director-level ops engaged — budget authority unclear",
        flag: "AMBER",
        points: 1,
      },
      {
        key: "technical_champion_only",
        label: "Technical champion (engineer or program lead) but no ops authority",
        flag: "AMBER",
        points: 1,
      },
      {
        key: "procurement_only",
        label: "Procurement only — no ops leadership access",
        flag: "SOFT_STOP",
        points: 0,
      },
    ],
  },
  {
    key: "aero_space_capital",
    cp: 7,
    industryTrigger: "Aerospace",
    subVertical: "new_space",
    label: "Aerospace (Space) — Capital runway and funding posture?",
    helpText:
      "Unlike a prime with a multi-year funded DoD contract, a pure commercial new space company may be 12–18 months from its next capital raise.",
    options: [
      {
        key: "funded_gt_24mo",
        label: "Well-funded — runway >24 months or public/DoD-anchored revenue",
        flag: "OK",
        points: 3,
      },
      {
        key: "funded_12_24mo",
        label: "12–24 months runway — monitoring raise timing",
        flag: "OK",
        points: 2,
      },
      {
        key: "funded_lt_12mo",
        label: "Under 12 months runway — raise in progress or imminent",
        flag: "AMBER",
        points: 0,
      },
      { key: "unknown", label: "Capital posture not known", flag: "AMBER", points: 0 },
    ],
  },
  {
    key: "aero_space_faa_ast",
    cp: 3,
    industryTrigger: "Aerospace",
    subVertical: "new_space",
    label: "Aerospace (Space) — FAA AST licensing and launch site readiness?",
    helpText:
      "FAA AST issues launch vehicle operator licenses and site licenses. License delays are a known constraint for new entrants.",
    options: [
      {
        key: "licensed_active",
        label: "FAA AST launch license active — no pending holds",
        flag: "OK",
        points: 2,
      },
      {
        key: "licensed_amendment_pending",
        label: "Licensed but vehicle amendment or payload review pending",
        flag: "AMBER",
        points: 1,
      },
      {
        key: "application_in_process",
        label: "License application in process — timeline uncertain",
        flag: "AMBER",
        points: 0,
      },
      {
        key: "na_no_launch",
        label: "Does not hold launch vehicle license (payload/satellite customer only)",
        flag: "OK",
        points: 1,
      },
    ],
  },
  {
    key: "aero_space_reusability",
    cp: 3,
    industryTrigger: "Aerospace",
    subVertical: "new_space",
    label: "Aerospace (Space) — Reusability architecture and supply chain implications?",
    helpText:
      "Reusable vehicles require refurbishment supply chains, inspection protocols, and re-qualification processes between flights — a distinct operational challenge from expendable launch.",
    options: [
      {
        key: "reusable_architecture",
        label: "Reusable architecture — refurb and re-qualification supply chain challenge",
        flag: "OK",
        points: 3,
      },
      {
        key: "expendable_architecture",
        label: "Expendable — traditional build-rate supply chain model",
        flag: "OK",
        points: 2,
      },
      {
        key: "hybrid_or_transitioning",
        label: "Transitioning or hybrid — architecture not fully decided",
        flag: "AMBER",
        points: 1,
      },
      { key: "unknown", label: "Not discussed", flag: "AMBER", points: 0 },
    ],
  },
  {
    key: "aero_space_competitive_moat",
    cp: 10,
    industryTrigger: "Aerospace",
    subVertical: "new_space",
    label: "Aerospace (Space) — Competitive position: how differentiated vs. SpaceX's vertically integrated model?",
    helpText:
      "SpaceX is vertically integrated and subsidizes commercial launch with Starlink revenue. Every other new space launch provider competes against this directly.",
    options: [
      {
        key: "defensible_niche",
        label: "Clear niche: small sat, polar, DoD classified, or rideshare aggregator",
        flag: "OK",
        points: 4,
      },
      {
        key: "medium_lift_competing",
        label: "Medium lift competing on price — exposed to SpaceX cost pressure",
        flag: "AMBER",
        points: 1,
      },
      {
        key: "dod_anchored",
        label: "DoD / Space Force contract anchored — programmatic protection",
        flag: "OK",
        points: 4,
      },
      {
        key: "unknown_positioning",
        label: "Competitive positioning unclear",
        flag: "AMBER",
        points: 0,
      },
    ],
  },
];

// =====================================================================
// INDUSTRY SUB-QUESTIONS — Building Materials (existing 3 keys, unchanged)
// =====================================================================
const BUILDING_MATERIALS_QUESTIONS: QuestionDef[] = [
  {
    // Moved from always-on CP1 — Decision 3 (bank-decisions-LOGGED.md)
    // Fires for both Building Materials and Industrial Manufacturing.
    key: "cp1_read_across",
    cp: 1,
    industryTrigger: ["Building Materials", "Industrial Manufacturing"],
    label: "Read-across frustration mentioned?",
    options: [
      { key: "yes", label: "Yes — explicitly raised", flag: "OK", points: 2 },
      { key: "hinted", label: "Hinted at", flag: "AMBER", points: 1 },
      { key: "no", label: "Not mentioned", flag: "AMBER", points: 0 },
    ],
  },
  {
    key: "mfg_multi_plant",
    cp: 1,
    industryTrigger: "Building Materials",
    label: "Mfg — Multi-plant with shared corporate P&L?",
    options: [
      {
        key: "yes",
        label: "Yes — multi-plant shared P&L",
        flag: "OK",
        points: 2,
      },
      { key: "single", label: "Single plant", flag: "AMBER", points: 0 },
      { key: "unknown", label: "Unknown", flag: "AMBER", points: 0 },
    ],
  },
  {
    key: "mfg_kaizen_team",
    cp: 4,
    industryTrigger: "Building Materials",
    label: "Mfg — Kaizen / CI team at corporate level?",
    options: [
      { key: "full", label: "Yes — full-time team", flag: "OK", points: 1 },
      { key: "part_time", label: "Part-time", flag: "AMBER", points: 1 },
      { key: "no", label: "None", flag: "AMBER", points: 0 },
    ],
  },
  {
    key: "mfg_recent_acquisition",
    cp: 1,
    industryTrigger: "Building Materials",
    label: "Mfg — Acquisition in last 24 months creating integration cost?",
    options: [
      {
        key: "yes",
        label: "Yes — integration challenge",
        flag: "OK",
        points: 2,
      },
      { key: "no", label: "No", flag: "OK", points: 0 },
      { key: "unknown", label: "Unknown", flag: "AMBER", points: 0 },
    ],
  },
];

// =====================================================================
// INDUSTRY SUB-QUESTIONS — Industrial Manufacturing
// Existing 3 spine keys retained + 25 new keys from expansion
// =====================================================================
const INDUSTRIAL_MFG_QUESTIONS: QuestionDef[] = [
  // ----- EXISTING SPINE (3 keys, unchanged) -----
  {
    key: "indmfg_multi_plant",
    cp: 1,
    industryTrigger: "Industrial Manufacturing",
    label: "Industrial Mfg — Multi-plant with shared corporate P&L?",
    options: [
      {
        key: "yes",
        label: "Yes — multi-plant shared P&L",
        flag: "OK",
        points: 2,
      },
      { key: "single", label: "Single plant", flag: "AMBER", points: 0 },
      { key: "unknown", label: "Unknown", flag: "AMBER", points: 0 },
    ],
  },
  {
    key: "indmfg_kaizen_team",
    cp: 4,
    industryTrigger: "Industrial Manufacturing",
    label: "Industrial Mfg — Kaizen / CI team at corporate level?",
    options: [
      { key: "full", label: "Yes — full-time team", flag: "OK", points: 1 },
      { key: "part_time", label: "Part-time", flag: "AMBER", points: 1 },
      { key: "no", label: "None", flag: "AMBER", points: 0 },
    ],
  },
  {
    key: "indmfg_recent_acquisition",
    cp: 1,
    industryTrigger: "Industrial Manufacturing",
    label: "Industrial Mfg — Acquisition in last 24 months creating integration cost?",
    options: [
      {
        key: "yes",
        label: "Yes — integration challenge",
        flag: "OK",
        points: 2,
      },
      { key: "no", label: "No", flag: "OK", points: 0 },
      { key: "unknown", label: "Unknown", flag: "AMBER", points: 0 },
    ],
  },

  // ----- NEW SPINE EXTENSIONS (3 questions) -----
  {
    key: "indmfg_capex_cycle",
    cp: 7,
    industryTrigger: "Industrial Manufacturing",
    label: "Industrial Mfg — Capital expenditure cycle phase?",
    helpText:
      "Heavy equipment manufacturers run capex in waves tied to commodity pricing, regulatory deadlines, or product refresh. Know where they are before talking ROI.",
    options: [
      {
        key: "active_expansion",
        label: "Active expansion / new capacity authorised",
        flag: "OK",
        points: 4,
      },
      {
        key: "replacement_refresh",
        label: "Replacement / refresh cycle underway",
        flag: "OK",
        points: 3,
      },
      {
        key: "maintenance_only",
        label: "Maintenance-only capex freeze",
        flag: "AMBER",
        points: 1,
      },
      { key: "unknown", label: "Unknown", flag: "AMBER", points: 0 },
    ],
  },
  {
    key: "indmfg_cost_program",
    cp: 3,
    industryTrigger: "Industrial Manufacturing",
    label: "Industrial Mfg — Named cost-out or margin-recovery programme?",
    helpText:
      "Public industrial companies almost always have a named programme (e.g., CAT's 'Lean' initiatives, CNH Industrial's 'Transform 2 Win'). If private, ask the CEO directly.",
    options: [
      {
        key: "named_public",
        label: "Named programme — public / announced",
        flag: "OK",
        points: 4,
      },
      {
        key: "named_internal",
        label: "Named programme — internal only (rep confirmed)",
        flag: "OK",
        points: 3,
      },
      {
        key: "ad_hoc",
        label: "Ad hoc cost reduction, no formal programme",
        flag: "AMBER",
        points: 1,
      },
      { key: "none", label: "No active programme", flag: "AMBER", points: 0 },
    ],
  },
  {
    key: "indmfg_footprint",
    cp: 1,
    industryTrigger: "Industrial Manufacturing",
    label: "Industrial Mfg — Manufacturing footprint — domestic, nearshore, or global?",
    helpText:
      "Reshoring or nearshoring signals capex. Global footprint with dispersed plants means the economic buyer may not be on-site.",
    options: [
      {
        key: "reshoring_active",
        label: "Active reshoring / nearshoring programme",
        flag: "OK",
        points: 3,
      },
      {
        key: "global_established",
        label: "Established global footprint, stable",
        flag: "OK",
        points: 2,
      },
      {
        key: "domestic_only",
        label: "Domestic single-country",
        flag: "OK",
        points: 1,
      },
      { key: "unknown", label: "Unknown", flag: "AMBER", points: 0 },
    ],
  },

  // ----- OFF-HIGHWAY SUB-VERTICAL (7 questions) -----
  {
    key: "offhwy_channel_model",
    cp: 4,
    industryTrigger: "Industrial Manufacturing",
    subVertical: "off_highway",
    label: "Off-Highway — Dealer network model vs direct sales?",
    helpText:
      "Caterpillar sells almost exclusively through independent dealers; Deere owns some dealers. Knowing the channel structure tells you who the real stakeholder map is.",
    options: [
      {
        key: "independent_dealer_dominant",
        label: "Independent dealer network — dominant channel",
        flag: "OK",
        points: 4,
      },
      {
        key: "mixed_dealer_direct",
        label: "Mixed: dealer network plus direct key accounts",
        flag: "OK",
        points: 3,
      },
      {
        key: "direct_dominant",
        label: "Primarily direct to end customer",
        flag: "AMBER",
        points: 1,
      },
      { key: "unknown", label: "Unknown", flag: "AMBER", points: 0 },
    ],
  },
  {
    key: "offhwy_aftermarket_ebit",
    cp: 7,
    industryTrigger: "Industrial Manufacturing",
    subVertical: "off_highway",
    label: "Off-Highway — Aftermarket parts and service as share of EBIT?",
    helpText:
      "Aftermarket is the high-margin engine. If the account cannot articulate aftermarket attach rate or parts-capture rate, they are flying blind on their most profitable line.",
    options: [
      {
        key: "tracked_and_improving",
        label: "Tracked — improving attach / capture rate",
        flag: "OK",
        points: 4,
      },
      {
        key: "tracked_flat",
        label: "Tracked — flat or declining",
        flag: "OK",
        points: 3,
      },
      {
        key: "not_tracked_separately",
        label: "Not tracked separately from new equipment revenue",
        flag: "AMBER",
        points: 1,
      },
      { key: "unknown", label: "Unknown", flag: "AMBER", points: 0 },
    ],
  },
  {
    key: "offhwy_tier4_rd",
    cp: 3,
    industryTrigger: "Industrial Manufacturing",
    subVertical: "off_highway",
    label: "Off-Highway — Tier 4 / Stage V engine compliance status and R&D cost burden?",
    helpText:
      "EPA Tier 4 Final and EU Stage V are sunk costs for most large OEMs. Next-cycle (hydrogen, electrification) is now the R&D budget debate.",
    options: [
      {
        key: "next_cycle_active_spend",
        label: "Next-cycle propulsion R&D actively funded and material",
        flag: "OK",
        points: 4,
      },
      {
        key: "compliance_complete_planning_next",
        label: "Tier 4 / Stage V complete — planning next cycle",
        flag: "OK",
        points: 3,
      },
      {
        key: "compliance_still_in_flight",
        label: "Still working through current emissions compliance",
        flag: "AMBER",
        points: 2,
      },
      {
        key: "not_applicable_no_engine",
        label: "Not applicable — component / attachment supplier, no engine",
        flag: "AMBER",
        points: 1,
      },
      { key: "unknown", label: "Unknown", flag: "AMBER", points: 0 },
    ],
  },
  {
    key: "offhwy_commodity_cycle",
    cp: 3,
    industryTrigger: "Industrial Manufacturing",
    subVertical: "off_highway",
    label: "Off-Highway — Commodity cycle exposure (construction, ag, mining)?",
    helpText:
      "Equipment demand is a derivative of end-market commodity prices. Knowing the cycle phase tells you whether the customer is in expansion or cost-cut mode.",
    options: [
      {
        key: "construction_upcycle",
        label: "Construction / infrastructure — active upcycle",
        flag: "OK",
        points: 4,
      },
      {
        key: "ag_strong",
        label: "Agriculture — strong commodity prices driving equipment replacement",
        flag: "OK",
        points: 3,
      },
      {
        key: "mining_expansion",
        label: "Mining — commodity-driven fleet expansion",
        flag: "OK",
        points: 3,
      },
      {
        key: "downcycle_cost_pressure",
        label: "End market in downcycle — customer in cost-preservation mode",
        flag: "SOFT_STOP",
        points: 0,
      },
      {
        key: "mixed_or_unknown",
        label: "Mixed exposure or unknown",
        flag: "AMBER",
        points: 1,
      },
    ],
  },
  {
    key: "offhwy_dealer_inventory",
    cp: 3,
    industryTrigger: "Industrial Manufacturing",
    subVertical: "off_highway",
    label: "Off-Highway — Dealer inventory days and retail delivery visibility?",
    helpText:
      "High dealer inventory is a leading indicator the OEM will cut production and go into cost-reduction mode. Low inventory signals demand strength and willingness to invest.",
    options: [
      {
        key: "low_healthy_demand",
        label: "Inventory lean — retail demand strong",
        flag: "OK",
        points: 4,
      },
      {
        key: "normalising",
        label: "Inventory normalising from peak — neutral",
        flag: "OK",
        points: 2,
      },
      {
        key: "elevated_building",
        label: "Dealer inventory elevated or building",
        flag: "AMBER",
        points: 1,
      },
      {
        key: "unknown",
        label: "Unknown — not tracked at this level",
        flag: "AMBER",
        points: 0,
      },
    ],
  },
  {
    key: "offhwy_telematics",
    cp: 10,
    industryTrigger: "Industrial Manufacturing",
    subVertical: "off_highway",
    label: "Off-Highway — Product support / telematics platform in place?",
    helpText:
      "Cat Connect, JDLink, Komatsu KOMTRAX, CNH AFS — proprietary telematics locks customers and dealers in. If the OEM has a mature telematics platform, any competitive solution must integrate or displace it.",
    options: [
      {
        key: "proprietary_mature",
        label: "Proprietary telematics platform — mature and widely deployed",
        flag: "OK",
        points: 3,
      },
      {
        key: "proprietary_building",
        label: "Building proprietary platform — gaps exist",
        flag: "OK",
        points: 4,
      },
      {
        key: "third_party_dependent",
        label: "Dependent on third-party telematics — no proprietary lock",
        flag: "AMBER",
        points: 2,
      },
      { key: "none", label: "No telematics programme", flag: "AMBER", points: 0 },
    ],
  },
  {
    key: "offhwy_cat_deere_lock",
    cp: 10,
    industryTrigger: "Industrial Manufacturing",
    subVertical: "off_highway",
    label: "Off-Highway — Competitive displacement risk from Cat or Deere dealer network?",
    helpText:
      "Caterpillar and Deere dealer relationships involve exclusive territory agreements, floor plan financing, and co-op marketing. Displacing an incumbent requires a specific value wedge.",
    options: [
      {
        key: "not_cat_deere_dependent",
        label: "Account not in Cat/Deere dealer ecosystem — open field",
        flag: "OK",
        points: 3,
      },
      {
        key: "adjacent_competing_channel",
        label: "Competes against Cat/Deere but maintains own channel",
        flag: "OK",
        points: 2,
      },
      {
        key: "cat_deere_certified_dealer",
        label: "Cat or Deere certified dealer — exclusive obligations",
        flag: "SOFT_STOP",
        points: 1,
      },
      {
        key: "unknown",
        label: "Unknown dealer certification status",
        flag: "AMBER",
        points: 0,
      },
    ],
  },

  // ----- POWER & ENERGY SUB-VERTICAL (7 questions) -----
  {
    key: "power_primary_segment",
    cp: 3,
    industryTrigger: "Industrial Manufacturing",
    subVertical: "power_energy",
    label: "Power & Energy — Primary customer segment: utility, commercial/industrial, or data center?",
    helpText:
      "Each segment has a different procurement motion, timeline, and approver. Utility = RFP + regulatory filing + 24-36 month cycle. Data center = facilities VP + 6-12 months.",
    options: [
      {
        key: "datacenter_dominant",
        label: "Data center / critical facility — dominant or fastest growing",
        flag: "OK",
        points: 4,
      },
      {
        key: "commercial_industrial",
        label: "Commercial / industrial (prime or standby power)",
        flag: "OK",
        points: 3,
      },
      {
        key: "utility_scale",
        label: "Utility-scale generation — primary customer",
        flag: "OK",
        points: 2,
      },
      {
        key: "mixed_equal",
        label: "Balanced across segments",
        flag: "AMBER",
        points: 1,
      },
      { key: "unknown", label: "Unknown", flag: "AMBER", points: 0 },
    ],
  },
  {
    key: "power_dc_tailwind",
    cp: 3,
    industryTrigger: "Industrial Manufacturing",
    subVertical: "power_energy",
    label: "Power & Energy — Backup power demand driven by data-center buildout?",
    helpText:
      "Generac, Cummins Power Systems, and Cat Power have all called out data-center as the fastest-growing demand signal.",
    options: [
      {
        key: "primary_growth_driver",
        label: "Data center demand — primary or top-two growth driver",
        flag: "OK",
        points: 4,
      },
      {
        key: "growing_but_secondary",
        label: "Growing but secondary to other segments",
        flag: "OK",
        points: 3,
      },
      {
        key: "not_a_focus",
        label: "Not a focus — no dedicated sales motion",
        flag: "AMBER",
        points: 1,
      },
      { key: "unknown", label: "Unknown", flag: "AMBER", points: 0 },
    ],
  },
  {
    key: "power_decarb_tension",
    cp: 3,
    industryTrigger: "Industrial Manufacturing",
    subVertical: "power_energy",
    label: "Power & Energy — Decarbonisation mandate vs ICE legacy product tension?",
    helpText:
      "Siemens Energy, GE Vernova, and Cummins all have public net-zero or carbon-reduction targets that create internal tension between legacy diesel/gas product revenue and new clean-power investment.",
    options: [
      {
        key: "clean_energy_majority_rd",
        label: "Clean / low-carbon products now majority of R&D spend",
        flag: "OK",
        points: 4,
      },
      {
        key: "transitioning_active_split",
        label: "Actively transitioning — meaningful clean energy R&D alongside legacy",
        flag: "OK",
        points: 3,
      },
      {
        key: "legacy_dominant_limited_clean",
        label: "Legacy ICE/diesel dominant — limited clean energy investment",
        flag: "AMBER",
        points: 1,
      },
      {
        key: "no_stated_target",
        label: "No public decarbonisation target or roadmap",
        flag: "AMBER",
        points: 0,
      },
    ],
  },
  {
    key: "power_ferc_gate",
    cp: 7,
    industryTrigger: "Industrial Manufacturing",
    subVertical: "power_energy",
    label: "Power & Energy — Regulatory / FERC approval as a procurement gate?",
    helpText:
      "Utility customers must pass FERC interconnection or state PUC approval before a power project can close. If contingent on an unapproved approval, revenue is not real yet.",
    options: [
      {
        key: "approval_complete_po_next",
        label: "Regulatory approval complete — PO / contract imminent",
        flag: "OK",
        points: 4,
      },
      {
        key: "approval_in_process",
        label: "Approval in process — defined timeline",
        flag: "OK",
        points: 2,
      },
      {
        key: "approval_not_started",
        label: "Approval not yet filed or in backlog",
        flag: "HARD_STOP",
        points: 0,
      },
      {
        key: "not_applicable_commercial",
        label: "Not applicable — commercial / industrial customer, no FERC gate",
        flag: "OK",
        points: 3,
      },
      { key: "unknown", label: "Unknown", flag: "AMBER", points: 0 },
    ],
  },
  {
    key: "power_sales_funnel_stage",
    cp: 8,
    industryTrigger: "Industrial Manufacturing",
    subVertical: "power_energy",
    label: "Power & Energy — Long-cycle sales process: where is this opportunity in the funnel?",
    helpText:
      "Power equipment for utility customers can spend two or more years in spec/design/approval before an RFP is issued. Knowing the true stage prevents phantom pipeline inflation.",
    options: [
      {
        key: "rfp_issued_or_awarded",
        label: "RFP issued or contract awarded",
        flag: "OK",
        points: 4,
      },
      {
        key: "spec_written_rfp_imminent",
        label: "Spec written — RFP imminent (confirmed timeline)",
        flag: "OK",
        points: 3,
      },
      {
        key: "early_design_budgeted",
        label: "In design phase — budget allocated, RFP not yet issued",
        flag: "AMBER",
        points: 2,
      },
      {
        key: "conceptual_no_budget",
        label: "Conceptual stage — no approved budget",
        flag: "SOFT_STOP",
        points: 0,
      },
      { key: "unknown", label: "Unknown", flag: "AMBER", points: 0 },
    ],
  },
  {
    key: "power_service_incumbent",
    cp: 10,
    industryTrigger: "Industrial Manufacturing",
    subVertical: "power_energy",
    label: "Power & Energy — Competitive incumbent: OEM-branded service or third-party MRO?",
    helpText:
      "GE Vernova and Siemens Energy hold long-term service agreements (LTSAs) on their installed base — 10-20 year agreements with annual escalators. Displacing an LTSA requires either contract expiry or a performance failure.",
    options: [
      {
        key: "ltsa_expiry_within_24mo",
        label: "LTSA / long-term service agreement expiring within 24 months",
        flag: "OK",
        points: 4,
      },
      {
        key: "third_party_mro_no_ltsa",
        label: "Third-party MRO — no OEM LTSA in place",
        flag: "OK",
        points: 3,
      },
      {
        key: "ltsa_active_3plus_years",
        label: "LTSA active — more than 3 years remaining",
        flag: "HARD_STOP",
        points: 0,
      },
      {
        key: "unknown",
        label: "Unknown LTSA status",
        flag: "AMBER",
        points: 0,
      },
    ],
  },
  {
    key: "power_aftermarket_share",
    cp: 7,
    industryTrigger: "Industrial Manufacturing",
    subVertical: "power_energy",
    label: "Power & Energy — Aftermarket / service revenue as share of total?",
    helpText:
      "GE Vernova has publicly stated that services carry significantly higher margins than equipment sales. If the account has not monetised its installed base through service, that is the economic wedge.",
    options: [
      {
        key: "service_majority_tracked",
        label: "Service / aftermarket majority of revenue — actively managed",
        flag: "OK",
        points: 4,
      },
      {
        key: "service_growing_tracked",
        label: "Service growing — tracked and a stated priority",
        flag: "OK",
        points: 3,
      },
      {
        key: "equipment_dominant_service_ad_hoc",
        label: "Equipment revenue dominant — service ad hoc",
        flag: "AMBER",
        points: 1,
      },
      { key: "unknown", label: "Unknown", flag: "AMBER", points: 0 },
    ],
  },

  // ----- MATERIAL HANDLING / INDUSTRIAL ROBOTICS SUB-VERTICAL (8 questions) -----
  {
    key: "mh_channel_integrator",
    cp: 4,
    industryTrigger: "Industrial Manufacturing",
    subVertical: "material_handling",
    label: "Material Handling / Robotics — Channel: direct to end customer or via system integrator?",
    helpText:
      "If the primary channel is system integrators, the SI is the organization that decides — not the warehouse operator or plant manager. Selling past the SI without their buy-in kills the deal.",
    options: [
      {
        key: "si_dominant",
        label: "System integrator dominant — SI designs and installs",
        flag: "OK",
        points: 4,
      },
      {
        key: "mixed_si_and_direct",
        label: "Mixed: SI channel plus direct key accounts",
        flag: "OK",
        points: 3,
      },
      {
        key: "direct_only",
        label: "Direct to end customer — no SI channel",
        flag: "OK",
        points: 2,
      },
      { key: "unknown", label: "Unknown", flag: "AMBER", points: 0 },
    ],
  },
  {
    key: "mh_si_mapped",
    cp: 3,
    industryTrigger: "Industrial Manufacturing",
    subVertical: "material_handling",
    label: "Material Handling / Robotics — SI / integrator partner — are we mapped to the right firm?",
    helpText:
      "ABB, Fanuc, and Universal Robots each have named integrator certification programmes. If the account uses a certified integrator we do not have a relationship with, we are invisible in the deal.",
    options: [
      {
        key: "si_relationship_confirmed",
        label: "SI relationship confirmed — warm path to decision maker",
        flag: "OK",
        points: 4,
      },
      {
        key: "si_identified_no_relationship",
        label: "SI identified — no existing relationship",
        flag: "AMBER",
        points: 2,
      },
      {
        key: "si_unknown",
        label: "SI not yet identified — deal structure unknown",
        flag: "HARD_STOP",
        points: 0,
      },
      {
        key: "direct_no_si",
        label: "Direct sale — SI not relevant",
        flag: "OK",
        points: 2,
      },
    ],
  },
  {
    key: "mh_automation_driver",
    cp: 3,
    industryTrigger: "Industrial Manufacturing",
    subVertical: "material_handling",
    label: "Material Handling / Robotics — Automation tailwind: reshoring, e-commerce, or labour cost driver?",
    helpText:
      "The urgency and budget size depend heavily on the driving force. Reshoring creates long-horizon greenfield capex. E-commerce throughput pressure creates short-fuse retrofits.",
    options: [
      {
        key: "labour_cost_immediate",
        label: "Labour cost / availability — active pressure, short timeline",
        flag: "OK",
        points: 4,
      },
      {
        key: "ecommerce_throughput",
        label: "E-commerce throughput gap — retrofit or greenfield imminent",
        flag: "OK",
        points: 4,
      },
      {
        key: "reshoring_greenfield",
        label: "Reshoring / new domestic facility — greenfield automation planned",
        flag: "OK",
        points: 3,
      },
      {
        key: "efficiency_programme",
        label: "Operational efficiency programme — no single urgent driver",
        flag: "AMBER",
        points: 2,
      },
      {
        key: "no_stated_driver",
        label: "No stated automation driver",
        flag: "AMBER",
        points: 0,
      },
    ],
  },
  {
    key: "mh_capex_model",
    cp: 7,
    industryTrigger: "Industrial Manufacturing",
    subVertical: "material_handling",
    label: "Material Handling / Robotics — Capex model: buy, lease, or robots-as-a-service (RaaS)?",
    helpText:
      "RaaS / subscription converts a capex decision into an opex decision — different approver, different budget pool, different CFO threshold.",
    options: [
      {
        key: "raas_preferred",
        label: "RaaS / subscription model preferred — opex buyer",
        flag: "OK",
        points: 4,
      },
      {
        key: "traditional_capex_buy",
        label: "Traditional capex purchase",
        flag: "OK",
        points: 3,
      },
      {
        key: "lease_financing",
        label: "Lease / equipment financing",
        flag: "OK",
        points: 3,
      },
      {
        key: "undecided_both_open",
        label: "Not decided — both models being evaluated",
        flag: "AMBER",
        points: 1,
      },
      { key: "unknown", label: "Unknown", flag: "AMBER", points: 0 },
    ],
  },
  {
    key: "mh_tco_model",
    cp: 7,
    industryTrigger: "Industrial Manufacturing",
    subVertical: "material_handling",
    label: "Material Handling / Robotics — Depreciation and TCO model in place?",
    helpText:
      "Sophisticated buyers have formal TCO models that include uptime, maintenance cost, integration cost, and resale value. Unsophisticated buyers need you to build the model for them.",
    options: [
      {
        key: "formal_tco_model_exists",
        label: "Formal TCO / depreciation model used by finance team",
        flag: "OK",
        points: 3,
      },
      {
        key: "informal_payback_calculation",
        label: "Informal payback period only — no full TCO",
        flag: "OK",
        points: 4,
      },
      {
        key: "no_model",
        label: "No TCO model — decision based on upfront price",
        flag: "AMBER",
        points: 1,
      },
      { key: "unknown", label: "Unknown", flag: "AMBER", points: 0 },
    ],
  },
  {
    key: "mh_incumbent_platform",
    cp: 10,
    industryTrigger: "Industrial Manufacturing",
    subVertical: "material_handling",
    label: "Material Handling / Robotics — Competitive incumbent: OEM robotics platform or third-party?",
    helpText:
      "Fanuc, KUKA, and ABB have deep software + support ecosystems that create switching costs beyond the hardware. Knowing the incumbent tells you whether you need a technical or commercial displacement argument.",
    options: [
      {
        key: "no_incumbent_greenfield",
        label: "Greenfield — no incumbent automation",
        flag: "OK",
        points: 4,
      },
      {
        key: "third_party_fragmented",
        label: "Third-party / fragmented — no single dominant platform",
        flag: "OK",
        points: 3,
      },
      {
        key: "ur_cobots_in_place",
        label: "Universal Robots cobots in place — UR ecosystem dependency",
        flag: "AMBER",
        points: 2,
      },
      {
        key: "fanuc_kuka_abb_locked",
        label: "Fanuc / KUKA / ABB platform deeply embedded — deep switching cost",
        flag: "SOFT_STOP",
        points: 1,
      },
      { key: "unknown", label: "Unknown", flag: "AMBER", points: 0 },
    ],
  },
  {
    key: "mh_safety_cert",
    cp: 5,
    industryTrigger: "Industrial Manufacturing",
    subVertical: "material_handling",
    label: "Material Handling / Robotics — Safety / regulatory certification requirement (CE, UL, OSHA)?",
    helpText:
      "Automated systems in warehouses and plants require CE marking (EU), UL listing, and OSHA-compliant risk assessment. Confirm before investing in a technical proof of concept.",
    options: [
      {
        key: "cert_confirmed_held",
        label: "Required certification confirmed and held",
        flag: "OK",
        points: 3,
      },
      {
        key: "cert_required_in_process",
        label: "Certification required — in process",
        flag: "OK",
        points: 2,
      },
      {
        key: "cert_required_not_held",
        label: "Certification required — not currently held",
        flag: "SOFT_STOP",
        points: 0,
      },
      {
        key: "not_required",
        label: "No specific certification required by customer",
        flag: "OK",
        points: 2,
      },
      {
        key: "unknown",
        label: "Unknown certification requirement",
        flag: "AMBER",
        points: 0,
      },
    ],
  },
  {
    key: "mh_service_model",
    cp: 7,
    industryTrigger: "Industrial Manufacturing",
    subVertical: "material_handling",
    label: "Material Handling / Robotics — Installed base service / maintenance model?",
    helpText:
      "Uptime SLAs in automated warehouses are typically 99%+. The service and maintenance model directly affects the total cost and the renewal / upsell motion.",
    options: [
      {
        key: "oem_service_contract_active",
        label: "OEM service contract — active and tracked",
        flag: "OK",
        points: 4,
      },
      {
        key: "si_maintains_customer_sla",
        label: "SI maintains and holds SLA with end customer",
        flag: "OK",
        points: 3,
      },
      {
        key: "in_house_no_contract",
        label: "In-house maintenance — no OEM or SI contract",
        flag: "AMBER",
        points: 1,
      },
      {
        key: "unknown",
        label: "Unknown service model",
        flag: "AMBER",
        points: 0,
      },
    ],
  },
];

// =====================================================================
// INDUSTRY SUB-QUESTIONS — Facility Management (existing 2 keys, unchanged)
// =====================================================================
const FACILITY_MGMT_QUESTIONS: QuestionDef[] = [
  {
    key: "fm_contract_or_owner",
    cp: 1,
    industryTrigger: "Facility Management",
    label: "FM — Contract-managed or owner-operated?",
    options: [
      { key: "contract", label: "Contract — client ROI story", flag: "OK", points: 2 },
      { key: "owner", label: "Owner-operated", flag: "OK", points: 1 },
    ],
  },
  {
    key: "fm_facility_count",
    cp: 1,
    industryTrigger: "Facility Management",
    label: "FM — Facility count?",
    options: [
      { key: "fifty_plus", label: "50+ facilities", flag: "OK", points: 2 },
      { key: "ten_to_49", label: "10–49 facilities", flag: "AMBER", points: 1 },
      {
        key: "under_10",
        label: "Under 10 facilities",
        flag: "AMBER",
        points: 0,
      },
    ],
  },
];

// =====================================================================
// INDUSTRY SUB-QUESTIONS — Medical Device (24 new keys, new vertical)
// =====================================================================
const MEDICAL_DEVICE_QUESTIONS: QuestionDef[] = [
  // Spine — fires for all Medical Device accounts
  {
    key: "meddev_qsr_status",
    cp: 3,
    industryTrigger: "Medical Device",
    label: "Medical Device — FDA quality system status (QSR / 21 CFR Part 820 / ISO 13485)?",
    helpText:
      "Ask whether they are FDA-registered and ISO 13485 certified — and whether their QMS is a strategic cost center or a source of recent audit findings.",
    options: [
      {
        key: "registered_clean",
        label: "FDA-registered, ISO 13485 certified, no recent 483s",
        flag: "OK",
        points: 3,
      },
      {
        key: "registered_findings",
        label: "Registered but open 483 observations or Warning Letter",
        flag: "HARD_STOP",
        points: 0,
      },
      {
        key: "registered_eumdr_gap",
        label: "Registered but EU MDR compliance not yet achieved",
        flag: "AMBER",
        points: 1,
      },
      {
        key: "unknown",
        label: "Unknown / not confirmed",
        flag: "AMBER",
        points: 0,
      },
    ],
  },
  {
    key: "meddev_eumdr_readiness",
    cp: 3,
    industryTrigger: "Medical Device",
    label: "Medical Device — EU MDR transition status?",
    helpText:
      "EU MDR (2017/745) requires Notified Body review for nearly all CE-marked devices. Stalled transitions signal regulatory bandwidth saturation that may compress discretionary spend.",
    options: [
      {
        key: "certified_or_na",
        label: "CE mark under MDR achieved, or EU market not in scope",
        flag: "OK",
        points: 2,
      },
      {
        key: "in_progress_on_track",
        label: "Transition in progress, Notified Body engaged, on track",
        flag: "OK",
        points: 1,
      },
      {
        key: "at_risk",
        label: "At risk — Notified Body slot not secured or dossier gaps",
        flag: "AMBER",
        points: 1,
      },
      {
        key: "legacy_mdd_only",
        label: "Still on MDD only, no MDR roadmap",
        flag: "SOFT_STOP",
        points: 0,
      },
      { key: "unknown", label: "Unknown", flag: "AMBER", points: 0 },
    ],
  },
  {
    key: "meddev_recall_history",
    cp: 3,
    industryTrigger: "Medical Device",
    label: "Medical Device — Recent recall or field safety corrective action (FSCA) in last 36 months?",
    helpText:
      "Class I or Class II recalls are public (FDA Recall database). A Class I recall on a primary product line signals acute engineering bandwidth drain and budget diversion.",
    options: [
      {
        key: "no_recall",
        label: "No recalls in last 36 months",
        flag: "OK",
        points: 2,
      },
      {
        key: "class_iii_only",
        label: "Class III (voluntary, low risk) only",
        flag: "OK",
        points: 1,
      },
      {
        key: "class_ii",
        label: "Class II recall — corrections/removals required",
        flag: "AMBER",
        points: 1,
      },
      {
        key: "class_i",
        label: "Class I recall — risk of serious injury or death",
        flag: "HARD_STOP",
        points: 0,
      },
      {
        key: "unknown",
        label: "Unknown — not researched",
        flag: "AMBER",
        points: 0,
      },
    ],
  },

  // Class III Implants sub-vertical (7 questions)
  {
    key: "implant_fda_pathway",
    cp: 3,
    industryTrigger: "Medical Device",
    subVertical: "class_iii_implants",
    label: "Medical Device (Class III Implants) — FDA PMA vs. 510(k) pathway for primary product?",
    helpText:
      "PMA products face far heavier post-market surveillance, supplemental PMA requirements for design changes, and longer pre-market timelines.",
    options: [
      {
        key: "pma_primary",
        label: "PMA — primary product line",
        flag: "OK",
        points: 3,
      },
      {
        key: "pma_and_510k",
        label: "Mixed portfolio: PMA flagship + 510(k) adjacents",
        flag: "OK",
        points: 2,
      },
      {
        key: "510k_only",
        label: "510(k) only — lower regulatory burden",
        flag: "AMBER",
        points: 1,
      },
      {
        key: "de_novo",
        label: "De Novo classification — novel with limited predicate",
        flag: "OK",
        points: 2,
      },
      { key: "unknown", label: "Unknown", flag: "AMBER", points: 0 },
    ],
  },
  {
    key: "implant_surgeon_adoption",
    cp: 4,
    industryTrigger: "Medical Device",
    subVertical: "class_iii_implants",
    label: "Medical Device (Class III Implants) — Surgeon adoption model: KOL-driven or protocol-standardized?",
    helpText:
      "KOL-driven accounts need surgeon engagement BEFORE supply chain can act. Protocol-standardized means supply chain or C-suite can mandate a switch. These are opposite buying dynamics.",
    options: [
      {
        key: "kol_driven_identified",
        label: "KOL-driven — key surgeons identified and warm",
        flag: "OK",
        points: 3,
      },
      {
        key: "kol_driven_cold",
        label: "KOL-driven — surgeons not yet engaged",
        flag: "AMBER",
        points: 1,
      },
      {
        key: "protocol_standardized",
        label: "Protocol/value analysis committee (VAC) drives decision",
        flag: "OK",
        points: 2,
      },
      {
        key: "mixed",
        label: "Mixed — some standardized, some surgeon-preference",
        flag: "AMBER",
        points: 1,
      },
      { key: "unknown", label: "Unknown", flag: "AMBER", points: 0 },
    ],
  },
  {
    key: "implant_clinical_evidence",
    cp: 3,
    industryTrigger: "Medical Device",
    subVertical: "class_iii_implants",
    label: "Medical Device (Class III Implants) — Current clinical evidence stage for target product?",
    helpText:
      "PMA approval without published RCT data limits hospital formulary access. Full registry data or published clinical evidence is a buying prerequisite for most IDN value analysis committees.",
    options: [
      {
        key: "rct_published",
        label: "RCT published in peer-reviewed journal",
        flag: "OK",
        points: 3,
      },
      {
        key: "registry_data",
        label: "Registry / real-world evidence available, no RCT",
        flag: "OK",
        points: 2,
      },
      {
        key: "pma_cleared_limited_data",
        label: "PMA cleared but limited post-market evidence",
        flag: "AMBER",
        points: 1,
      },
      {
        key: "ide_study_ongoing",
        label: "IDE clinical study ongoing — not yet cleared",
        flag: "SOFT_STOP",
        points: 0,
      },
      { key: "unknown", label: "Unknown", flag: "AMBER", points: 0 },
    ],
  },
  {
    key: "implant_contracting_vehicle",
    cp: 4,
    industryTrigger: "Medical Device",
    subVertical: "class_iii_implants",
    label: "Medical Device (Class III Implants) — Hospital contracting vehicle: direct, GPO, or IDN-negotiated?",
    helpText:
      "Surgeon-preference implants often bypass GPO contracts — surgeon loyalty is the contract. If the IDN is pushing standardization, who controls the VAC process matters enormously.",
    options: [
      {
        key: "surgeon_preference_direct",
        label: "Surgeon preference — direct hospital purchase, no GPO",
        flag: "OK",
        points: 3,
      },
      {
        key: "idn_standardized",
        label: "IDN standardization program underway or complete",
        flag: "OK",
        points: 2,
      },
      {
        key: "gpo_contract_held",
        label: "GPO contract held (e.g., Vizient, Premier, Intalere)",
        flag: "OK",
        points: 2,
      },
      {
        key: "no_contract",
        label: "No established contract — ad hoc purchasing",
        flag: "AMBER",
        points: 1,
      },
      { key: "unknown", label: "Unknown", flag: "AMBER", points: 0 },
    ],
  },
  {
    key: "implant_gross_margin",
    cp: 7,
    industryTrigger: "Medical Device",
    subVertical: "class_iii_implants",
    label: "Medical Device (Class III Implants) — Gross margin trajectory on implant portfolio?",
    helpText:
      "Class III implants command 60-80%+ gross margins historically. ASP erosion from IDN standardization or bundled payment pressure is the primary economic threat.",
    options: [
      {
        key: "stable_or_expanding",
        label: "Stable or expanding — pricing power intact",
        flag: "OK",
        points: 3,
      },
      {
        key: "modest_erosion",
        label: "Modest erosion (< 2pts/year) — manageable",
        flag: "OK",
        points: 2,
      },
      {
        key: "significant_erosion",
        label: "Significant erosion — ASP pressure from IDN/CMS",
        flag: "AMBER",
        points: 1,
      },
      {
        key: "bundle_payment_disruption",
        label: "Bundled payment program compressing implant revenue",
        flag: "AMBER",
        points: 1,
      },
      { key: "unknown", label: "Unknown", flag: "AMBER", points: 0 },
    ],
  },
  {
    key: "implant_reimbursement",
    cp: 7,
    industryTrigger: "Medical Device",
    subVertical: "class_iii_implants",
    label: "Medical Device (Class III Implants) — Reimbursement pathway: established DRG or emerging/at-risk coverage?",
    helpText:
      "Without established CMS DRG coverage, hospitals absorb implant cost. Emerging technology add-on payment (NTAP) or breakthrough device status changes the economic calculus completely.",
    options: [
      {
        key: "established_drg",
        label: "Established DRG / CPT coverage — no reimbursement risk",
        flag: "OK",
        points: 3,
      },
      {
        key: "ntap_or_breakthrough",
        label: "NTAP or Breakthrough Device designation — coverage expanding",
        flag: "OK",
        points: 2,
      },
      {
        key: "coverage_gap",
        label: "Coverage gap — payer-by-payer negotiation required",
        flag: "AMBER",
        points: 1,
      },
      {
        key: "no_coverage",
        label: "No established coverage pathway — hospital absorbs cost",
        flag: "HARD_STOP",
        points: 0,
      },
      { key: "unknown", label: "Unknown", flag: "AMBER", points: 0 },
    ],
  },
  {
    key: "implant_switching_cost",
    cp: 10,
    industryTrigger: "Medical Device",
    subVertical: "class_iii_implants",
    label: "Medical Device (Class III Implants) — Switching cost and incumbent depth?",
    helpText:
      "In implants, switching cost is measured in surgeon retraining, proctoring requirements, loaner tray replacement, and rep relationship depth.",
    options: [
      {
        key: "greenfield_no_incumbent",
        label: "Greenfield — no incumbent for this product type",
        flag: "OK",
        points: 4,
      },
      {
        key: "incumbent_early",
        label: "Incumbent present but <18 months, limited surgeon training",
        flag: "OK",
        points: 2,
      },
      {
        key: "incumbent_established_open",
        label: "Incumbent established but surgeon dissatisfaction documented",
        flag: "OK",
        points: 2,
      },
      {
        key: "incumbent_entrenched",
        label: "Entrenched incumbent — surgeons trained, trays in OR, >3 years",
        flag: "AMBER",
        points: 1,
      },
      {
        key: "sole_source_contract",
        label: "Sole-source contract in force — legally blocked",
        flag: "HARD_STOP",
        points: 0,
      },
      { key: "unknown", label: "Unknown", flag: "AMBER", points: 0 },
    ],
  },

  // Capital Equipment / Imaging sub-vertical (7 questions)
  {
    key: "capimg_capex_cycle",
    cp: 3,
    industryTrigger: "Medical Device",
    subVertical: "capital_imaging",
    label: "Medical Device (Capital Imaging) — Hospital capex cycle phase for target equipment category?",
    helpText:
      "Capital equipment decisions follow hospital capex planning cycles — typically annual approval with 6-18 month evaluation. A freshly installed system means a 7+ year wait.",
    options: [
      {
        key: "actively_evaluating",
        label: "Active evaluation or RFP in market",
        flag: "OK",
        points: 4,
      },
      {
        key: "budget_approved_planning",
        label: "Budget approved, not yet in market",
        flag: "OK",
        points: 3,
      },
      {
        key: "early_planning",
        label: "Early planning — 12-24 months out",
        flag: "OK",
        points: 2,
      },
      {
        key: "recently_refreshed",
        label: "Recently refreshed — <3 years since last install",
        flag: "AMBER",
        points: 1,
      },
      {
        key: "no_refresh_planned",
        label: "No refresh in 5+ year horizon",
        flag: "SOFT_STOP",
        points: 0,
      },
      { key: "unknown", label: "Unknown", flag: "AMBER", points: 0 },
    ],
  },
  {
    key: "capimg_idn_vs_site",
    cp: 4,
    industryTrigger: "Medical Device",
    subVertical: "capital_imaging",
    label: "Medical Device (Capital Imaging) — IDN-level decision or site-level?",
    helpText:
      "IDN-level decisions involve C-suite, supply chain, and technology officers. Site-level decisions can be faster but lack enterprise leverage.",
    options: [
      {
        key: "idn_enterprise",
        label: "IDN/enterprise decision — C-suite + technology committee",
        flag: "OK",
        points: 3,
      },
      {
        key: "site_with_idn_influence",
        label: "Site-led with IDN ratification required",
        flag: "OK",
        points: 2,
      },
      {
        key: "site_autonomous",
        label: "Site-autonomous — department chief + CFO sign-off",
        flag: "OK",
        points: 1,
      },
      { key: "unknown", label: "Unknown", flag: "AMBER", points: 0 },
    ],
  },
  {
    key: "capimg_gpo_contract",
    cp: 4,
    industryTrigger: "Medical Device",
    subVertical: "capital_imaging",
    label: "Medical Device (Capital Imaging) — GPO contract position for this equipment category?",
    helpText:
      "GPO contracts (Vizient, Premier, Healthtrust) can accelerate deals or lock out non-preferred vendors. Confirm whether the target hospital is GPO-committed and whether the manufacturer holds preferred status.",
    options: [
      {
        key: "preferred_on_gpo",
        label: "Manufacturer holds preferred GPO contract for this category",
        flag: "OK",
        points: 3,
      },
      {
        key: "gpo_member_open",
        label: "Hospital is GPO member, category contract is open/competitive",
        flag: "OK",
        points: 2,
      },
      {
        key: "gpo_competitor_preferred",
        label: "Competitor holds preferred GPO status",
        flag: "AMBER",
        points: 1,
      },
      {
        key: "no_gpo_direct",
        label: "No GPO involvement — direct negotiation",
        flag: "OK",
        points: 2,
      },
      { key: "unknown", label: "Unknown", flag: "AMBER", points: 0 },
    ],
  },
  {
    key: "capimg_service_attach",
    cp: 7,
    industryTrigger: "Medical Device",
    subVertical: "capital_imaging",
    label: "Medical Device (Capital Imaging) — Service revenue model and attach rate?",
    helpText:
      "Service contracts on imaging equipment often generate 40-60% margins and represent recurring revenue that outlasts the capital sale. Third-party or self-serve = a competitive opening.",
    options: [
      {
        key: "oem_service_contracted",
        label: "Full OEM service contract in place, high attach",
        flag: "OK",
        points: 3,
      },
      {
        key: "partial_oem_service",
        label: "Partial OEM service — some categories third-party",
        flag: "OK",
        points: 2,
      },
      {
        key: "third_party_iam",
        label: "Third-party independent service (IAM) managing fleet",
        flag: "AMBER",
        points: 1,
      },
      {
        key: "self_maintained",
        label: "Hospital self-maintains — minimal service revenue",
        flag: "AMBER",
        points: 1,
      },
      { key: "unknown", label: "Unknown", flag: "AMBER", points: 0 },
    ],
  },
  {
    key: "capimg_ai_strategy",
    cp: 3,
    industryTrigger: "Medical Device",
    subVertical: "capital_imaging",
    label: "Medical Device (Capital Imaging) — AI/software overlay strategy: own platform or open ecosystem?",
    helpText:
      "The battle in capital imaging has shifted to AI-powered reading software, workflow platforms, and cloud connectivity. Manufacturers with proprietary AI lock-in (Edison, teamplay, HealthSuite) are more defensible.",
    options: [
      {
        key: "proprietary_ai_embedded",
        label: "Proprietary AI platform embedded — data flywheel advantage",
        flag: "OK",
        points: 3,
      },
      {
        key: "open_ecosystem_partner",
        label: "Open ecosystem — partners with third-party AI vendors",
        flag: "OK",
        points: 2,
      },
      {
        key: "hardware_only",
        label: "Hardware-only — no software or AI play",
        flag: "AMBER",
        points: 1,
      },
      { key: "unknown", label: "Unknown", flag: "AMBER", points: 0 },
    ],
  },
  {
    key: "capimg_incumbent",
    cp: 10,
    industryTrigger: "Medical Device",
    subVertical: "capital_imaging",
    label: "Medical Device (Capital Imaging) — Incumbent depth and contract lock-in?",
    helpText:
      "Capital imaging incumbents win renewals more than 70% of the time. Understand the installed fleet age, service contract expiry, and any vendor of record agreements.",
    options: [
      {
        key: "end_of_life_system",
        label: "Incumbent system at or past recommended replacement age",
        flag: "OK",
        points: 4,
      },
      {
        key: "mid_life_service_friction",
        label: "Mid-life system but documented service dissatisfaction",
        flag: "OK",
        points: 3,
      },
      {
        key: "new_install_competitor",
        label: "Competitor installed within 3 years — strong lock-in",
        flag: "AMBER",
        points: 1,
      },
      {
        key: "vendor_of_record",
        label: "Vendor-of-record agreement in force — legally preferenced",
        flag: "AMBER",
        points: 0,
      },
      {
        key: "exclusive_contract",
        label: "Exclusive multi-year agreement, no competitive evaluation allowed",
        flag: "HARD_STOP",
        points: 0,
      },
      { key: "unknown", label: "Unknown", flag: "AMBER", points: 0 },
    ],
  },
  {
    key: "capimg_cyber_524b",
    cp: 3,
    industryTrigger: "Medical Device",
    subVertical: "capital_imaging",
    label: "Medical Device (Capital Imaging) — Cybersecurity / FDA 524B premarket submission status?",
    helpText:
      "FDA Section 524B requires cybersecurity documentation in all premarket submissions for network-connected devices (effective March 2023). Hospital IT and biomedical engineering now gate capital approvals on this.",
    options: [
      {
        key: "fully_compliant_published",
        label: "524B-compliant, cybersecurity documentation published",
        flag: "OK",
        points: 2,
      },
      {
        key: "in_progress",
        label: "Compliance in progress — timeline confirmed",
        flag: "OK",
        points: 1,
      },
      {
        key: "not_addressed",
        label: "Not yet addressed — gap in hospital IT approval process",
        flag: "AMBER",
        points: 0,
      },
      { key: "unknown", label: "Unknown", flag: "AMBER", points: 0 },
    ],
  },

  // Disposables & High-Volume sub-vertical (7 questions)
  {
    key: "disp_gpo_status",
    cp: 7,
    industryTrigger: "Medical Device",
    subVertical: "disposables",
    label: "Medical Device (Disposables) — GPO contract status: preferred, tier, or off-contract?",
    helpText:
      "GPO preferred status (Tier 1 on Vizient, Premier, Healthtrust) drives the vast majority of acute care volume for disposables.",
    options: [
      {
        key: "tier1_gpo",
        label: "Tier 1 preferred on major GPO(s) — strong share commitment",
        flag: "OK",
        points: 4,
      },
      {
        key: "tier2_gpo",
        label: "Tier 2 on major GPO(s) — contested, below-preferred pricing",
        flag: "OK",
        points: 2,
      },
      {
        key: "off_contract_competing",
        label: "Off-contract, competing for position — RFP in market",
        flag: "OK",
        points: 3,
      },
      {
        key: "lost_gpo_contract",
        label: "Lost major GPO contract — volume at risk",
        flag: "SOFT_STOP",
        points: 0,
      },
      { key: "unknown", label: "Unknown", flag: "AMBER", points: 0 },
    ],
  },
  {
    key: "disp_eto_sterilization",
    cp: 3,
    industryTrigger: "Medical Device",
    subVertical: "disposables",
    label: "Medical Device (Disposables) — EtO sterilization capacity: owned, contracted, or at-risk?",
    helpText:
      "Post-EPA proposed EtO emission rules, many contract sterilizers have curtailed or closed capacity. Manufacturers dependent on contract EtO without alternatives face production risk.",
    options: [
      {
        key: "owned_facility_compliant",
        label: "Owned EtO facility — compliant with current NESHAP, no closure risk",
        flag: "OK",
        points: 2,
      },
      {
        key: "contracted_secured",
        label: "Contract sterilization secured — multi-year agreements in place",
        flag: "OK",
        points: 2,
      },
      {
        key: "alternative_validated",
        label: "EtO alternative modality validated (gamma, e-beam, VHP)",
        flag: "OK",
        points: 3,
      },
      {
        key: "contracted_at_risk",
        label: "Contract sterilization — limited alternatives, capacity at risk",
        flag: "AMBER",
        points: 1,
      },
      {
        key: "dependent_no_alt",
        label: "EtO-dependent, no alternative validated, supply risk documented",
        flag: "AMBER",
        points: 0,
      },
      { key: "unknown", label: "Unknown", flag: "AMBER", points: 0 },
    ],
  },
  {
    key: "disp_china_sourcing",
    cp: 3,
    industryTrigger: "Medical Device",
    subVertical: "disposables",
    label: "Medical Device (Disposables) — China sourcing exposure on key raw materials or sub-assemblies?",
    helpText:
      "Disposables manufacturing often depends on China-sourced resins, needles, tubing, or packaging. Tariff risk and supply disruption (COVID precedent, UFLPA enforcement) are board-level topics.",
    options: [
      {
        key: "low_exposure_diversified",
        label: "<20% COGS from China, diversified supply base",
        flag: "OK",
        points: 2,
      },
      {
        key: "moderate_active_mitigation",
        label: "Moderate exposure (20-50%) but active near-shoring program",
        flag: "OK",
        points: 1,
      },
      {
        key: "high_exposure_no_mitigation",
        label: ">50% China-sourced, limited mitigation plan",
        flag: "AMBER",
        points: 0,
      },
      {
        key: "unknown",
        label: "Unknown / not discussed",
        flag: "AMBER",
        points: 0,
      },
    ],
  },
  {
    key: "disp_price_compression",
    cp: 7,
    industryTrigger: "Medical Device",
    subVertical: "disposables",
    label: "Medical Device (Disposables) — Volume / price compression rate on core SKUs?",
    helpText:
      "Disposables face relentless annual price reductions under GPO contract terms — often 1-3% per year mandatory.",
    options: [
      {
        key: "volume_offsetting_price",
        label: "Volume growth outpacing price erosion — net revenue expanding",
        flag: "OK",
        points: 3,
      },
      {
        key: "flat_net_revenue",
        label: "Volume roughly offsetting price — net revenue flat",
        flag: "OK",
        points: 1,
      },
      {
        key: "price_winning_erosion",
        label: "Price compression exceeding volume gains — net revenue declining",
        flag: "AMBER",
        points: 1,
      },
      {
        key: "unit_economics_negative",
        label: "Negative unit economics on key SKUs — considering rationalization",
        flag: "AMBER",
        points: 0,
      },
      { key: "unknown", label: "Unknown", flag: "AMBER", points: 0 },
    ],
  },
  {
    key: "disp_channel_mix",
    cp: 3,
    industryTrigger: "Medical Device",
    subVertical: "disposables",
    label: "Medical Device (Disposables) — Channel mix: acute care vs. ambulatory vs. retail/pharmacy?",
    helpText:
      "Acute care is GPO-dominated and margin-thin. ASCs are higher-margin and growing but require a different sales motion. Channel diversification signals commercial sophistication.",
    options: [
      {
        key: "acute_plus_asc_plus_retail",
        label: "Multi-channel: acute + ASC + retail/pharmacy",
        flag: "OK",
        points: 3,
      },
      {
        key: "acute_plus_asc",
        label: "Acute + ASC, growing ambulatory",
        flag: "OK",
        points: 2,
      },
      {
        key: "acute_only",
        label: "Acute care only — GPO-dependent, limited channel diversification",
        flag: "AMBER",
        points: 1,
      },
      {
        key: "retail_only",
        label: "Retail/pharmacy only — different regulatory dynamics",
        flag: "OK",
        points: 1,
      },
      { key: "unknown", label: "Unknown", flag: "AMBER", points: 0 },
    ],
  },
  {
    key: "disp_switching_churn",
    cp: 10,
    industryTrigger: "Medical Device",
    subVertical: "disposables",
    label: "Medical Device (Disposables) — Competitive switching rate and contract churn?",
    helpText:
      "Disposables churn at GPO contract renewal (typically 3-year cycles). The switching cost is low. Understand when the target account's GPO contract renews and whether the incumbent is defending or vulnerable.",
    options: [
      {
        key: "gpo_renewal_within_12mo",
        label: "GPO contract renewal within 12 months — active evaluation window",
        flag: "OK",
        points: 4,
      },
      {
        key: "gpo_renewal_12_24mo",
        label: "GPO renewal 12-24 months out — influence window open",
        flag: "OK",
        points: 3,
      },
      {
        key: "incumbent_strong_gpo",
        label: "Incumbent in strong GPO position, renewal >24 months out",
        flag: "AMBER",
        points: 1,
      },
      {
        key: "no_gpo_local_contracts",
        label: "Local contracts only — must win account-by-account",
        flag: "OK",
        points: 2,
      },
      { key: "unknown", label: "Unknown", flag: "AMBER", points: 0 },
    ],
  },
  {
    key: "disp_pms_maturity",
    cp: 3,
    industryTrigger: "Medical Device",
    subVertical: "disposables",
    label: "Medical Device (Disposables) — Post-market surveillance and complaint management maturity?",
    helpText:
      "High-volume disposables generate high complaint volumes by sheer unit count. Immature PMS systems create MDR reporting backlogs, FDA audit exposure, and recall risk.",
    options: [
      {
        key: "automated_pms_system",
        label: "Automated PMS/complaint system integrated with MDR reporting",
        flag: "OK",
        points: 2,
      },
      {
        key: "manual_but_current",
        label: "Manual but up-to-date — no backlog, current MDR filings",
        flag: "OK",
        points: 1,
      },
      {
        key: "backlog_or_gaps",
        label: "Known MDR reporting backlog or identified PMS gaps",
        flag: "AMBER",
        points: 0,
      },
      { key: "unknown", label: "Unknown", flag: "AMBER", points: 0 },
    ],
  },
];

// =====================================================================
// Master export — ALL_QUESTIONS
// =====================================================================
export const ALL_QUESTIONS: QuestionDef[] = [
  ...CP1_QUESTIONS,
  ...CP2_QUESTIONS,
  ...CP3_QUESTIONS,
  ...CP4_QUESTIONS,
  ...CP5_QUESTIONS,
  ...CP6_QUESTIONS,
  ...CP7_QUESTIONS,
  ...CP8_QUESTIONS,
  ...CP9_QUESTIONS,
  ...CP10_QUESTIONS,
  ...AUTOMOTIVE_QUESTIONS,
  ...AEROSPACE_QUESTIONS,
  ...BUILDING_MATERIALS_QUESTIONS,
  ...INDUSTRIAL_MFG_QUESTIONS,
  ...FACILITY_MGMT_QUESTIONS,
  ...MEDICAL_DEVICE_QUESTIONS,
];

// =====================================================================
// getActiveQuestions — unchanged from V3, sub-vertical filtering
// is handled by tenant config layer, not here.
// =====================================================================
export function getActiveQuestions(args: {
  industry: string | null | undefined;
  customerPath: string | null | undefined;
}): QuestionDef[] {
  const { industry, customerPath } = args;
  return ALL_QUESTIONS.filter((q) => {
    // industry filter
    if (q.industryTrigger) {
      if (Array.isArray(q.industryTrigger)) {
        if (!industry || !q.industryTrigger.includes(industry)) return false;
      } else {
        if (q.industryTrigger !== industry) return false;
      }
    }
    // fastlane: skip CPs 3, 4, 5, 8, 9 (CP6 Demonstration runs on fastlane only if scope changed)
    if (customerPath === "fastlane") {
      if ([3, 4, 5, 8, 9].includes(q.cp)) return false;
    }
    if (q.skipOnFastlane && customerPath === "fastlane") return false;
    return true;
  });
}

// =====================================================================
// CP status derivation — unchanged from V3
// =====================================================================
export type CpStatusValue = "green" | "amber" | "red" | null;

export function deriveCpStatus(args: {
  cp: number;
  industry: string | null | undefined;
  customerPath: string | null | undefined;
  answers: Map<string, { selectedOption: string | null; isAssumption: boolean }>;
}): CpStatusValue {
  const { cp, industry, customerPath, answers } = args;
  const cpQuestions = getActiveQuestions({ industry, customerPath }).filter((q) => q.cp === cp);
  if (cpQuestions.length === 0) return null;

  let answeredCount = 0;
  let hasHardStop = false;
  let hasSoftStop = false;
  let hasAssumption = false;
  let allOk = true;

  for (const q of cpQuestions) {
    const ans = answers.get(q.key);
    if (!ans || !ans.selectedOption) {
      allOk = false;
      continue;
    }
    answeredCount += 1;
    const opt = q.options.find((o) => o.key === ans.selectedOption);
    if (!opt) continue;
    if (opt.flag === "HARD_STOP") hasHardStop = true;
    if (opt.flag === "SOFT_STOP") hasSoftStop = true;
    if (opt.flag !== "OK") allOk = false;
    if (ans.isAssumption) hasAssumption = true;
  }

  if (answeredCount === 0) return null;
  if (hasHardStop) return "red";
  if (hasSoftStop) return "amber";
  if (hasAssumption) return "amber";
  if (allOk && answeredCount === cpQuestions.length) return "green";
  return "amber";
}

// =====================================================================
// cpRawPoints / cpMaxPoints — unchanged from V3
// =====================================================================
export function cpRawPoints(args: {
  cp: number;
  industry: string | null | undefined;
  customerPath: string | null | undefined;
  answers: Map<string, { selectedOption: string | null }>;
}): number {
  const { cp, industry, customerPath, answers } = args;
  const cpQuestions = getActiveQuestions({ industry, customerPath }).filter((q) => q.cp === cp);
  let total = 0;
  for (const q of cpQuestions) {
    const ans = answers.get(q.key);
    if (!ans || !ans.selectedOption) continue;
    const opt = q.options.find((o) => o.key === ans.selectedOption);
    if (opt) total += opt.points;
  }
  return total;
}

export function cpMaxPoints(args: {
  cp: number;
  industry: string | null | undefined;
  customerPath: string | null | undefined;
}): number {
  const cpQuestions = getActiveQuestions(args).filter((q) => q.cp === args.cp);
  let total = 0;
  for (const q of cpQuestions) {
    const max = Math.max(...q.options.map((o) => o.points));
    total += max;
  }
  return total;
}

// =====================================================================
// Hunt list catalog — extended with new industry items
// =====================================================================
export interface HuntItemDef {
  itemKey: string;
  label: string;
  linkedCp: number | null;
  industryTrigger?: string | null;
  subVertical?: string | null;
}

export const HUNT_CATALOG: HuntItemDef[] = [
  // Always-on (10) — unchanged from V3
  { itemKey: "revenue_verified", label: "Account revenue (filings / ZoomInfo / LinkedIn verified)", linkedCp: 1 },
  { itemKey: "multi_site_count", label: "Multi-site / plant count (read-across sizing)", linkedCp: 1 },
  { itemKey: "legal_screen", label: "Legal / sanctions / compliance screen run", linkedCp: 1 },
  { itemKey: "geography", label: "Geography / contracting entity (NA / EU)", linkedCp: 1 },
  { itemKey: "incumbent_solution", label: "Active competitor subscription + contract length", linkedCp: 9 },
  { itemKey: "mi6_relationship", label: "Existing Mi6 relationship (warm / referral / cold)", linkedCp: 2 },
  { itemKey: "compelling_event_evidence", label: "Timeline evidence (link / doc / quote)", linkedCp: 8 },
  { itemKey: "org_chart", label: "Org chart with Finance + Ops + HR mapped", linkedCp: 2 },
  { itemKey: "champion_quote", label: "Champion's exact words on pain (verbatim)", linkedCp: 2 },
  { itemKey: "eb_veto_signal", label: "EB veto authority signal (org structure / quote)", linkedCp: 2 },

  // PE and Healthcare entries removed 2026-05-07 (off-domain).

  // Automotive (2) — unchanged
  { itemKey: "auto_oem_letter", label: "Automotive — OEM cost-down letter / mandate evidence", linkedCp: 1, industryTrigger: "Automotive" },
  { itemKey: "auto_network_match", label: "Automotive — Bryan's network coverage match", linkedCp: 2, industryTrigger: "Automotive" },

  // Building Materials (2) — unchanged
  { itemKey: "mfg_acquisition_history", label: "Mfg — recent acquisition history (24mo)", linkedCp: 1, industryTrigger: "Building Materials" },
  { itemKey: "mfg_corp_vs_plant_pl", label: "Mfg — corporate vs. plant P&L structure", linkedCp: 1, industryTrigger: "Building Materials" },

  // Industrial Manufacturing (2) — unchanged
  { itemKey: "indmfg_acquisition_history", label: "Industrial Mfg — recent acquisition history (24mo)", linkedCp: 1, industryTrigger: "Industrial Manufacturing" },
  { itemKey: "indmfg_corp_vs_plant_pl", label: "Industrial Mfg — corporate vs. plant P&L structure", linkedCp: 1, industryTrigger: "Industrial Manufacturing" },

  // NEW — Aerospace Commercial (5)
  { itemKey: "aero_com_hunt_buildrate_announcements", label: "Boeing / Airbus public build-rate announcements for primary program (investor calls, press releases, FAA production certificates)", linkedCp: 3, industryTrigger: "Aerospace", subVertical: "commercial" },
  { itemKey: "aero_com_hunt_nadcap_audit_date", label: "NADCAP audit expiry date (Performance Review Institute portal or supplier quality team)", linkedCp: 3, industryTrigger: "Aerospace", subVertical: "commercial" },
  { itemKey: "aero_com_hunt_10k_oem_concentration", label: "Customer concentration in 10-K or investor materials — Boeing/Airbus as % of revenue", linkedCp: 7, industryTrigger: "Aerospace", subVertical: "commercial" },
  { itemKey: "aero_com_hunt_der_approval_list", label: "DER/DAR relationships on file — can they navigate an FAA Form 8110 change without external DER hire?", linkedCp: 10, industryTrigger: "Aerospace", subVertical: "commercial" },
  { itemKey: "aero_com_hunt_spirit_collins_vendor", label: "Approved Vendor List (AVL) status with Spirit AeroSystems, Collins, Honeywell Aero, or Safran", linkedCp: 1, industryTrigger: "Aerospace", subVertical: "commercial" },

  // NEW — Aerospace Defense (5)
  { itemKey: "aero_def_hunt_usaspending", label: "USAspending.gov contract award search — prime and subcontract awards, contract type (FFP vs. CPFF), period of performance", linkedCp: 7, industryTrigger: "Aerospace", subVertical: "defense" },
  { itemKey: "aero_def_hunt_sam_cage", label: "SAM.gov CAGE code registration, ITAR status, and active exclusion check", linkedCp: 3, industryTrigger: "Aerospace", subVertical: "defense" },
  { itemKey: "aero_def_hunt_10k_backlog", label: "10-K or annual report funded backlog, unfunded backlog, and contract type mix", linkedCp: 7, industryTrigger: "Aerospace", subVertical: "defense" },
  { itemKey: "aero_def_hunt_dcaa_audit", label: "DCAA audit findings (publicly available under FOIA — search DCAA audit reports database or open CAR status from DCMA)", linkedCp: 3, industryTrigger: "Aerospace", subVertical: "defense" },
  { itemKey: "aero_def_hunt_program_press", label: "DoD program press releases and POGO / Defense News reporting on program restructures, milestone decisions, or contract re-competitions", linkedCp: 3, industryTrigger: "Aerospace", subVertical: "defense" },

  // NEW — Aerospace Space (4)
  { itemKey: "aero_space_hunt_crunchbase_funding", label: "Crunchbase or PitchBook last funding round date, amount, and lead investor — proxy for runway and growth phase", linkedCp: 7, industryTrigger: "Aerospace", subVertical: "new_space" },
  { itemKey: "aero_space_hunt_faa_ast_license", label: "FAA AST launch vehicle operator license status (FAA.gov commercial space launch licenses database — public)", linkedCp: 3, industryTrigger: "Aerospace", subVertical: "new_space" },
  { itemKey: "aero_space_hunt_sda_space_force", label: "Space Force and SDA contract awards on SAM.gov / USAspending.gov — indicates revenue durability and DoD program-of-record transition", linkedCp: 7, industryTrigger: "Aerospace", subVertical: "new_space" },
  { itemKey: "aero_space_hunt_launch_manifest", label: "Public launch manifest or customer disclosure (company website, SpaceflightNow, NASASpaceflight) — cadence proxy and customer concentration signal", linkedCp: 3, industryTrigger: "Aerospace", subVertical: "new_space" },

  // NEW — Industrial Manufacturing Off-Highway (6)
  { itemKey: "offhwy_catdeere_10k_segment", label: "Pull Cat, Deere, CNH Industrial, AGCO latest 10-K / 20-F segment revenue and EBIT for Machinery vs Financial Products vs Parts", linkedCp: 7, industryTrigger: "Industrial Manufacturing", subVertical: "off_highway" },
  { itemKey: "offhwy_dealer_directory", label: "Map authorized dealer network territory for target OEM — use OEM dealer locator + state licensing filings", linkedCp: 4, industryTrigger: "Industrial Manufacturing", subVertical: "off_highway" },
  { itemKey: "offhwy_epa_tier4_dates", label: "EPA Tier 4 Final engine compliance effective dates by horsepower band — confirm target account's engine range", linkedCp: 3, industryTrigger: "Industrial Manufacturing", subVertical: "off_highway" },
  { itemKey: "offhwy_aftermarket_attach", label: "Find public disclosure of dealer parts-capture rate or aftermarket revenue as % of total — check investor day presentations", linkedCp: 7, industryTrigger: "Industrial Manufacturing", subVertical: "off_highway" },
  { itemKey: "offhwy_telematics_platform", label: "Identify which telematics platform is deployed (Cat Connect, JDLink, KOMTRAX, etc.) and integration API availability", linkedCp: 10, industryTrigger: "Industrial Manufacturing", subVertical: "off_highway" },
  { itemKey: "offhwy_commodity_index", label: "Pull current ISM Manufacturing PMI + construction spending data + copper/coal/grain price trend as proxy for end-market cycle phase", linkedCp: 3, industryTrigger: "Industrial Manufacturing", subVertical: "off_highway" },

  // NEW — Industrial Manufacturing Power & Energy (6)
  { itemKey: "power_ferc_interconnect_queue", label: "Pull FERC interconnection queue data for target utility region — identify project backlog and expected clearing dates", linkedCp: 7, industryTrigger: "Industrial Manufacturing", subVertical: "power_energy" },
  { itemKey: "power_ge_siemens_ltsa_terms", label: "Research standard GE Vernova / Siemens Energy LTSA contract terms (escalator structure, termination provisions) — use public rate case filings and annual reports", linkedCp: 10, industryTrigger: "Industrial Manufacturing", subVertical: "power_energy" },
  { itemKey: "power_generac_cummins_dc_segment", label: "Pull Generac and Cummins Power Systems investor day slides for data-center backup power segment revenue split and growth guidance", linkedCp: 3, industryTrigger: "Industrial Manufacturing", subVertical: "power_energy" },
  { itemKey: "power_decarb_roadmap", label: "Find public net-zero or emissions-reduction roadmap for target company (sustainability report, 10-K risk factors, CDP disclosure)", linkedCp: 3, industryTrigger: "Industrial Manufacturing", subVertical: "power_energy" },
  { itemKey: "power_puc_rate_case", label: "Check state PUC rate case filings for utility customers in target region — identifies budgeted infrastructure projects and approval timelines", linkedCp: 8, industryTrigger: "Industrial Manufacturing", subVertical: "power_energy" },
  { itemKey: "power_installed_base", label: "Estimate installed base by OEM model (GE 7F/9F, Siemens SGT, Cat 3500 series) and map to LTSA expiry windows using public plant permit filings", linkedCp: 10, industryTrigger: "Industrial Manufacturing", subVertical: "power_energy" },

  // NEW — Industrial Manufacturing Material Handling (6)
  { itemKey: "mh_integrator_directory", label: "Pull ABB Robotics, Fanuc, and Universal Robots certified integrator directories — identify which firms are active in target account's geography and vertical", linkedCp: 4, industryTrigger: "Industrial Manufacturing", subVertical: "material_handling" },
  { itemKey: "mh_symbotic_kion_dematic_10k", label: "Pull Symbotic 10-K, KION Group annual report, and Dematic investor materials for segment revenue, gross margin, and service attach rate", linkedCp: 7, industryTrigger: "Industrial Manufacturing", subVertical: "material_handling" },
  { itemKey: "mh_raas_pricing_benchmarks", label: "Research published RaaS pricing structures (Locus Robotics, Vecna, 6 River Systems) for AMR-as-a-service per-pick or per-robot-hour rates as TCO comparison baseline", linkedCp: 7, industryTrigger: "Industrial Manufacturing", subVertical: "material_handling" },
  { itemKey: "mh_osha_amr_guidelines", label: "Confirm current OSHA and ANSI/ITSDF guidelines for AMR / AGV safety in mixed-pedestrian environments — identify any pending rule changes", linkedCp: 5, industryTrigger: "Industrial Manufacturing", subVertical: "material_handling" },
  { itemKey: "mh_reshoring_capex_map", label: "Use Reshoring Initiative tracker or Site Selection Group data to identify announced greenfield manufacturing facilities in target geography — active automation buying events", linkedCp: 3, industryTrigger: "Industrial Manufacturing", subVertical: "material_handling" },
  { itemKey: "mh_fanuc_kuka_switching_cost", label: "Research public case studies or analyst reports estimating Fanuc / KUKA / ABB platform switching cost (programming migration, teach pendant retraining, wrist tooling compatibility)", linkedCp: 10, industryTrigger: "Industrial Manufacturing", subVertical: "material_handling" },

  // NEW — Medical Device spine (4)
  { itemKey: "meddev_fda_warning_letter", label: "FDA Warning Letters / Consent Decrees — check enforcement database", linkedCp: 3, industryTrigger: "Medical Device" },
  { itemKey: "meddev_recall_database", label: "FDA MedWatch Recall Database — product-line recall class and date", linkedCp: 3, industryTrigger: "Medical Device" },
  { itemKey: "meddev_iso13485_cert", label: "ISO 13485 certificate — issuing body, scope, expiry date (BSI, TÜV, NSF, SGS)", linkedCp: 3, industryTrigger: "Medical Device" },
  { itemKey: "meddev_eumdr_notifiedbody", label: "Notified Body assignment for EU MDR — published in NANDO database", linkedCp: 3, industryTrigger: "Medical Device" },

  // NEW — Medical Device Class III Implants (6)
  { itemKey: "implant_pma_database", label: "FDA 510(k) / PMA database — cleared date, predicate, product codes for target product", linkedCp: 3, industryTrigger: "Medical Device", subVertical: "class_iii_implants" },
  { itemKey: "implant_open_payments", label: "CMS Open Payments database — surgeon consulting fees, royalty relationships, research payments from manufacturer", linkedCp: 4, industryTrigger: "Medical Device", subVertical: "class_iii_implants" },
  { itemKey: "implant_cms_drg_coverage", label: "CMS DRG / NTAP / Breakthrough Device designation — coverage determination date and scope", linkedCp: 7, industryTrigger: "Medical Device", subVertical: "class_iii_implants" },
  { itemKey: "implant_idn_vac_minutes", label: "IDN Value Analysis Committee decision record — product standardization votes, incumbent contract expiry", linkedCp: 4, industryTrigger: "Medical Device", subVertical: "class_iii_implants" },
  { itemKey: "implant_earnings_asp", label: "Earnings call / investor day — ASP trends, volume vs. price commentary, geographic mix", linkedCp: 7, industryTrigger: "Medical Device", subVertical: "class_iii_implants" },
  { itemKey: "implant_recall_maude", label: "FDA MAUDE database — adverse event reports on target product line; pattern = signal", linkedCp: 3, industryTrigger: "Medical Device", subVertical: "class_iii_implants" },

  // NEW — Medical Device Capital Imaging (6)
  { itemKey: "capimg_gpo_awards", label: "GPO contract award announcements — Vizient, Premier, Healthtrust preferred vendor lists for imaging categories", linkedCp: 4, industryTrigger: "Medical Device", subVertical: "capital_imaging" },
  { itemKey: "capimg_service_revenue_pct", label: "Earnings calls / investor presentations — service revenue as % of total, service margin commentary", linkedCp: 7, industryTrigger: "Medical Device", subVertical: "capital_imaging" },
  { itemKey: "capimg_510k_ai_clearances", label: "FDA 510(k) database — AI/software as a medical device (SaMD) clearances filed by manufacturer in last 24 months", linkedCp: 3, industryTrigger: "Medical Device", subVertical: "capital_imaging" },
  { itemKey: "capimg_hospital_capex_plan", label: "Target hospital's capital plan — CMS cost reports, bond prospectus, or annual report for capital expenditure line", linkedCp: 8, industryTrigger: "Medical Device", subVertical: "capital_imaging" },
  { itemKey: "capimg_fleet_age", label: "Installed fleet age — biomedical equipment management database or facilities disclosure", linkedCp: 10, industryTrigger: "Medical Device", subVertical: "capital_imaging" },
  { itemKey: "capimg_cyber_nhsac", label: "HC3 / HHS threat briefs — named vulnerabilities in competing imaging platforms; use to build cybersecurity urgency narrative", linkedCp: 3, industryTrigger: "Medical Device", subVertical: "capital_imaging" },

  // NEW — Medical Device Disposables (5)
  { itemKey: "disp_gpo_contract_awards", label: "GPO contract award press releases — Vizient, Premier, Healthtrust sourcing event outcomes for relevant categories", linkedCp: 7, industryTrigger: "Medical Device", subVertical: "disposables" },
  { itemKey: "disp_eto_facility_map", label: "FDA-registered sterilization facility list — 21 CFR Part 11 EO sterilizers, cross-reference with EPA enforcement actions", linkedCp: 3, industryTrigger: "Medical Device", subVertical: "disposables" },
  { itemKey: "disp_uflpa_customs", label: "CBP UFLPA entity list and forced labor enforcement actions — China-sourced component exposure", linkedCp: 3, industryTrigger: "Medical Device", subVertical: "disposables" },
  { itemKey: "disp_recall_rate", label: "FDA recall database — rolling 36-month recall count and classification for target SKU families", linkedCp: 3, industryTrigger: "Medical Device", subVertical: "disposables" },
  { itemKey: "disp_channel_expansion", label: "ASC / ambulatory surgery center accreditation filings and GPO affiliation — identify channel shift signal", linkedCp: 4, industryTrigger: "Medical Device", subVertical: "disposables" },
];

export function getActiveHuntItems(industry: string | null | undefined): HuntItemDef[] {
  return HUNT_CATALOG.filter((h) => !h.industryTrigger || h.industryTrigger === industry);
}
